import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./RunAssessment.css";
import { getSelectedCompany } from "../auth";
import { apiRequest } from "../api";

const PERF_DEBUG = process.env.REACT_APP_PERF_DEBUG === "1";

const DEFAULT_BRAND_LOGO_URL =
  "https://valhallaplus.org/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fvalhalla-icon.c13cd40e.png&w=64&q=75";
const OPTION_MORE_INFO_TOOLTIP_DELAY_MS = 3000;
const API_BASE = process.env.REACT_APP_API_URL_BASE;
const PATIENTS_API = `${API_BASE}/api/patients/`;
const DOCUMENT_UPLOAD_GET_LINK_API = `${API_BASE}/api/document-upload/get-link/`;
const PATIENT_ASSESSMENT_ATTEMPTS_API = `${API_BASE}/api/patient-assessment-attempts/`;
const PATIENT_RESPONSES_API = `${API_BASE}/api/patient-responses/`;
const PATIENT_RESPONSES_HISTORY_API = `${API_BASE}/api/patient-responses-history/`;
const ASSESSMENT_CLASSIFICATIONS_API = `${API_BASE}/api/assessment-classifications/`;
const EMAIL_SEND_WITH_ATTACHMENT_API = `${API_BASE}/api/email/send-with-attachment`;

const normalizePrefillPrompt = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .replace(/^[¿¡]+/, "")
    .replace(/[?.!:]+$/g, "")
    .trim();

  return normalized;
};

const AGE_PREFILL_PROMPTS = new Set([
  "your age",
  "age",
  "su edad",
  "edad",
]);

const DOB_PREFILL_PROMPTS = new Set([
  "your date of birth",
  "date of birth",
  "su fecha de nacimiento",
  "fecha de nacimiento",
]);

const INJURY_EVENT_DATE_PREFILL_PROMPTS = new Set([
  "date of injury",
  "injury date",
  "date of event",
  "event date",
  "fecha de la lesion",
  "fecha de lesion",
  "fecha del incidente",
  "fecha del evento",
]);

const REFERRAL_COMPANY_PREFILL_PROMPTS = new Set([
  "provider or clinic referring you",
  "referred by",
  "company referral",
  "proveedor o clinica que lo refiere",
  "proveedor o clinica que le refirio",
  "referido por",
  "empresa de referencia",
  "compania de referencia",
]);

const matchesPrefillPrompt = (prompt, titlePrompt, aliases) =>
  aliases.has(prompt) || aliases.has(titlePrompt);

const isInjuryEventDatePrompt = (value) => {
  const normalized = normalizePrefillPrompt(value);
  return INJURY_EVENT_DATE_PREFILL_PROMPTS.has(normalized);
};

const parseDateOnlyYmd = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value) return null;

  const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) {
    const year = Number(ymdMatch[1]);
    const month = Number(ymdMatch[2]);
    const day = Number(ymdMatch[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    return {
      year,
      month,
      day,
      ymd: `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    };
  }

  try {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    const year = parsed.getFullYear();
    const month = parsed.getMonth() + 1;
    const day = parsed.getDate();
    return {
      year,
      month,
      day,
      ymd: `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    };
  } catch {
    return null;
  }
};

const calculateAgeFromDob = (dobParts) => {
  if (!dobParts) return null;

  const now = new Date();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;
  const nowDay = now.getDate();

  let age = nowYear - dobParts.year;
  if (nowMonth < dobParts.month || (nowMonth === dobParts.month && nowDay < dobParts.day)) {
    age -= 1;
  }

  if (!Number.isFinite(age) || age < 0 || age > 130) return null;
  return age;
};

const normalizeQuestionType = (question) =>
  String(question?.question_type?.description ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const toSnakeCase = (value) =>
  String(value ?? "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();

const normalizeStructuredAgreementSource = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return typeof value === "object" && !Array.isArray(value) ? value : null;
};

const getCaseInsensitiveValue = (source, targetKey) => {
  if (!source || typeof source !== "object") return undefined;

  const normalizedTargetKey = String(targetKey ?? "").trim().toLowerCase();
  const entry = Object.entries(source).find(
    ([key]) => String(key ?? "").trim().toLowerCase() === normalizedTargetKey
  );

  return entry ? entry[1] : undefined;
};

const getOrderedPrefixedEntries = (source, prefix) => {
  if (!source || typeof source !== "object") return [];

  const matcher = new RegExp(`^${prefix}(\\d+)$`, "i");

  return Object.entries(source)
    .map(([key, value]) => {
      const match = String(key ?? "").match(matcher);
      return match
        ? {
            key,
            order: Number(match[1]),
            value,
          }
        : null;
    })
    .filter((entry) => entry && Number.isFinite(entry.order))
    .sort((left, right) => left.order - right.order);
};

const buildPatientTemplateData = (patient, prefillContext) => {
  const templateData = {};

  if (patient && typeof patient === "object") {
    Object.entries(patient).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      templateData[key] = value;
      templateData[String(key).toLowerCase()] = value;
      templateData[toSnakeCase(key)] = value;
    });
  }

  if (prefillContext?.firstName) {
    templateData.first_name = prefillContext.firstName;
    templateData.firstname = prefillContext.firstName;
    templateData.firstName = prefillContext.firstName;
  }

  if (prefillContext?.lastName) {
    templateData.last_name = prefillContext.lastName;
    templateData.lastname = prefillContext.lastName;
    templateData.lastName = prefillContext.lastName;
  }

  return templateData;
};

const interpolatePatientTemplateValue = (value, patientTemplateData) =>
  String(value ?? "").replace(/<([^<>]+)>/g, (match, token) => {
    const normalizedToken = String(token ?? "").trim();
    if (!normalizedToken) return "";

    const candidateKeys = [
      normalizedToken,
      normalizedToken.toLowerCase(),
      toSnakeCase(normalizedToken),
    ];

    for (const candidateKey of candidateKeys) {
      if (Object.prototype.hasOwnProperty.call(patientTemplateData, candidateKey)) {
        const resolvedValue = patientTemplateData[candidateKey];
        return resolvedValue === undefined || resolvedValue === null ? "" : String(resolvedValue);
      }
    }

    return match;
  });

const renderAgreementTextWithBreaks = (value, patientTemplateData, keyPrefix) => {
  const interpolatedText = interpolatePatientTemplateValue(value, patientTemplateData);
  const lines = interpolatedText.split(/<br\s*\/?\s*>/i);

  return lines.map((line, index) => (
    <React.Fragment key={`${keyPrefix}-${index}`}>
      {index > 0 ? <br /> : null}
      {line}
    </React.Fragment>
  ));
};

const formatDateMmDdYyyy = (value = new Date()) => {
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  const year = String(value.getFullYear());
  return `${month}/${day}/${year}`;
};

const sanitizePdfFilename = (value, fallback = "signature_agreement") => {
  const normalized = String(value ?? "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "");

  return normalized || fallback;
};

const createSafeGuidPdfFilename = () => {
  const cryptoUuid =
    typeof window !== "undefined" ? window.crypto?.randomUUID?.() : undefined;
  const fallbackUuid = `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`;
  const safeUuid = sanitizePdfFilename(cryptoUuid || fallbackUuid, "signature_agreement");

  return `${safeUuid}.pdf`;
};

const getPresignedUploadErrorMessage = (error, label = "Document upload") => {
  const message = String(error?.message || "");
  if (error instanceof TypeError || /Failed to fetch/i.test(message)) {
    const browserOrigin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "the current origin";
    return `${label} was blocked by S3 CORS policy. Configure the S3 bucket to allow OPTIONS and PUT from ${browserOrigin}, including the Content-Type header.`;
  }

  return message || `${label} failed.`;
};

const getEmailRequestErrorMessage = async (response) => {
  try {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const payload = await response.json();
      if (typeof payload === "string") {
        return payload;
      }

      if (Array.isArray(payload)) {
        return payload.join(" ");
      }

      if (payload && typeof payload === "object") {
        return Object.values(payload)
          .flatMap((value) => (Array.isArray(value) ? value : [value]))
          .map((value) => String(value ?? "").trim())
          .filter(Boolean)
          .join(" ");
      }
    }

    return await response.text();
  } catch {
    return "";
  }
};

const escapeEmailHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildSignatureAgreementEmailHtml = ({ firstName, languageCode = "en" }) => {
  const isSpanish = String(languageCode ?? "en").trim().toLowerCase() === "es";
  const safeFirstName = escapeEmailHtml(
    String(firstName ?? "").trim() || (isSpanish ? "" : "there")
  );

  if (isSpanish) {
    return `<!DOCTYPE html>
<html lang="es">
  <body style="margin:0; padding:0; background-color:#f4f7fb; font-family:Arial, Helvetica, sans-serif; color:#1f2937;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#f4f7fb; margin:0; padding:24px 0;">
      <tr>
        <td align="center">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:640px; background-color:#ffffff; border-radius:12px; overflow:hidden; box-shadow:0 4px 18px rgba(15, 23, 42, 0.08);">
            <tr>
              <td style="background-color:#0f172a; padding:28px 36px; text-align:center;">
                <h1 style="margin:0; font-size:24px; line-height:1.3; color:#ffffff; font-weight:700;">
                  Su Acuerdo de Servicios de Valhalla Health
                </h1>
                <p style="margin:10px 0 0; font-size:14px; color:#cbd5e1;">
                  Copia confirmada adjunta
                </p>
              </td>
            </tr>
            <tr>
              <td style="padding:36px;">
                <p style="margin:0 0 20px; font-size:16px; line-height:1.6;">Hola${safeFirstName ? ` ${safeFirstName}` : ""},</p>
                <p style="margin:0 0 16px; font-size:16px; line-height:1.7;">Gracias por completar su documentación con Valhalla Health. Adjunto a este correo electrónico encontrará su Acuerdo de Servicios de Valhalla Health firmado para sus archivos.</p>
                <div style="margin:0 0 24px; padding:20px; background-color:#f8fafc; border:1px solid #e2e8f0; border-radius:10px;">
                  <p style="margin:0; font-size:16px; line-height:1.7; color:#334155;">Este acuerdo confirma las condiciones bajo las cuales Valhalla Health prestará servicios relacionados con su caso de lesiones personales y describe cómo se garantiza el pago de dichos servicios mediante los fondos de su acuerdo. Usted no es personalmente responsable de ningún saldo; su gravamen es sin recurso y está vinculado únicamente al resultado de su caso.</p>
                </div>
                <p style="margin:0 0 16px; font-size:16px; line-height:1.7;">Conserve este documento si aún no lo ha hecho. Si tiene alguna pregunta sobre el acuerdo o su cuenta, no dude en comunicarse directamente con nosotros.</p>
                <p style="margin:28px 0 0; font-size:16px; line-height:1.7;">Saludos cordiales,<br /><strong>Valhalla Health</strong></p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
  }

  return `<!DOCTYPE html>
<html lang="en">
  <body style="margin:0; padding:0; background-color:#f4f7fb; font-family:Arial, Helvetica, sans-serif; color:#1f2937;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#f4f7fb; margin:0; padding:24px 0;">
      <tr>
        <td align="center">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:640px; background-color:#ffffff; border-radius:12px; overflow:hidden; box-shadow:0 4px 18px rgba(15, 23, 42, 0.08);">
            <tr>
              <td style="background-color:#0f172a; padding:28px 36px; text-align:center;">
                <h1 style="margin:0; font-size:24px; line-height:1.3; color:#ffffff; font-weight:700;">
                  Your Valhalla Health Service Agreement
                </h1>
                <p style="margin:10px 0 0; font-size:14px; color:#cbd5e1;">
                  Confirmed Copy Enclosed
                </p>
              </td>
            </tr>
            <tr>
              <td style="padding:36px;">
                <p style="margin:0 0 20px; font-size:16px; line-height:1.6;">Hi ${safeFirstName},</p>
                <p style="margin:0 0 16px; font-size:16px; line-height:1.7;">Thank you for completing your documentation with Valhalla Health. Attached to this email is your signed Valhalla Health Service Agreement for your records.</p>
                <div style="margin:0 0 24px; padding:20px; background-color:#f8fafc; border:1px solid #e2e8f0; border-radius:10px;">
                  <p style="margin:0; font-size:16px; line-height:1.7; color:#334155;">This agreement confirms the arrangement under which Valhalla Health will provide services in connection with your personal injury case and outlines how payment for those services is secured through your settlement proceeds. You are not personally responsible for any balance; your lien is non-recourse and tied solely to the outcome of your case.</p>
                </div>
                <p style="margin:0 0 16px; font-size:16px; line-height:1.7;">Please retain this document if you have not done so already. If you have any questions about the agreement or your account, do not hesitate to reach out to us directly.</p>
                <p style="margin:28px 0 0; font-size:16px; line-height:1.7;">Warm regards,<br /><strong>Valhalla Health</strong></p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
};

const sendSignatureAgreementEmail = async ({
  requestFn,
  recipientEmail,
  downloadUrl,
  fileName,
  firstName,
  languageCode = "en",
}) => {
  const trimmedRecipientEmail = String(recipientEmail ?? "").trim();
  const trimmedDownloadUrl = String(downloadUrl ?? "").trim();
  const trimmedFileName = String(fileName ?? "Agreement Document.pdf").trim() || "Agreement Document.pdf";
  const isSpanish = String(languageCode ?? "en").trim().toLowerCase() === "es";

  if (!trimmedRecipientEmail || !trimmedDownloadUrl) {
    return false;
  }

  const payload = {
    to_addresses: [trimmedRecipientEmail],
    subject: isSpanish
      ? "Su Acuerdo de Servicios de Valhalla Health - Copia confirmada adjunta"
      : "Your Valhalla Health Service Agreement - Confirmed Copy Enclosed",
    body_html: buildSignatureAgreementEmailHtml({ firstName, languageCode }),
    attachments: [{
      download_url: trimmedDownloadUrl,
      file_name: trimmedFileName,
      content_type: "application/pdf",
    }],
  };

  const response = await requestFn(EMAIL_SEND_WITH_ATTACHMENT_API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (response.ok) {
    return true;
  }

  const lastError = await getEmailRequestErrorMessage(response);
  throw new Error(lastError || "Signature agreement email failed.");
};

const updateAttemptAgreementStatus = async ({
  requestFn,
  attemptId,
  agreementSigned,
  agreementSent,
}) => {
  const resolvedAttemptId = Number(attemptId) || 0;
  if (!resolvedAttemptId) {
    return false;
  }

  const response = await requestFn(`${PATIENT_ASSESSMENT_ATTEMPTS_API}${resolvedAttemptId}/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      agreement_sent: Boolean(agreementSent),
      agreement_signed: Boolean(agreementSigned),
    }),
  });

  if (response.ok) {
    return true;
  }

  const message = await response.text().catch(() => "");
  throw new Error(`Agreement status update failed (${response.status}) ${message}`);
};

const AgreementDocumentContent = ({
  agreementDefinition,
  displayDocumentName,
  patientData,
  includeSignatureSection = false,
  questionText = "",
  signatureDataUrl = "",
  fullName = "",
  dateText = "",
}) => {
  const titleLineOne = getCaseInsensitiveValue(agreementDefinition, "title_1");
  const titleLineTwo = getCaseInsensitiveValue(agreementDefinition, "title_2");
  const summaryText = getCaseInsensitiveValue(agreementDefinition, "summary");
  const headingEntries = getOrderedPrefixedEntries(agreementDefinition, "heading_");

  return (
    <div className="run-assessment-agreement-content">
      <div className="run-assessment-agreement-content-page">
        <div className="run-assessment-agreement-content-header">
          <div className="run-assessment-agreement-content-title-primary">
            {renderAgreementTextWithBreaks(
              titleLineOne || displayDocumentName,
              patientData,
              "agreement-title-1"
            )}
          </div>
          {titleLineTwo ? (
            <div className="run-assessment-agreement-content-title-secondary">
              {renderAgreementTextWithBreaks(
                titleLineTwo,
                patientData,
                "agreement-title-2"
              )}
            </div>
          ) : null}
        </div>

        {summaryText ? (
          <div className="run-assessment-agreement-content-summary">
            {renderAgreementTextWithBreaks(
              summaryText,
              patientData,
              "agreement-summary"
            )}
          </div>
        ) : null}

        {headingEntries.map((headingEntry) => {
          const headingValue =
            headingEntry?.value && typeof headingEntry.value === "object"
              ? headingEntry.value
              : {};
          const headingTitle = getCaseInsensitiveValue(headingValue, "title");
          const numberedItems = getOrderedPrefixedEntries(headingValue, "number_item_");

          return (
            <section key={headingEntry.key} className="run-assessment-agreement-section">
              {headingTitle ? (
                <div className="run-assessment-agreement-section-title">
                  {renderAgreementTextWithBreaks(
                    headingTitle,
                    patientData,
                    `${headingEntry.key}-title`
                  )}
                </div>
              ) : null}

              <ol className="run-assessment-agreement-numbered-list">
                {numberedItems.map((numberedItem) => {
                  const itemValue =
                    numberedItem?.value && typeof numberedItem.value === "object"
                      ? numberedItem.value
                      : {};
                  const itemTitle = getCaseInsensitiveValue(itemValue, "title");
                  const paragraphs = getOrderedPrefixedEntries(itemValue, "paragraph_");

                  return (
                    <li
                      key={numberedItem.key}
                      className="run-assessment-agreement-numbered-item"
                      value={numberedItem.order}
                    >
                      {itemTitle ? (
                        <div className="run-assessment-agreement-numbered-item-title">
                          {renderAgreementTextWithBreaks(
                            itemTitle,
                            patientData,
                            `${headingEntry.key}-${numberedItem.key}-title`
                          )}
                        </div>
                      ) : null}

                      <div className="run-assessment-agreement-numbered-item-body">
                        {paragraphs.map((paragraphEntry) => (
                          <p
                            key={paragraphEntry.key}
                            className="run-assessment-agreement-paragraph"
                          >
                            {renderAgreementTextWithBreaks(
                              paragraphEntry.value,
                              patientData,
                              `${headingEntry.key}-${numberedItem.key}-${paragraphEntry.key}`
                            )}
                          </p>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          );
        })}

        {includeSignatureSection ? (
          <section className="run-assessment-agreement-section run-assessment-agreement-signature-section">
            <div className="run-assessment-agreement-section-title">SIGNATURE</div>
            {questionText ? (
              <p className="run-assessment-agreement-paragraph run-assessment-agreement-signature-copy">
                {questionText}
              </p>
            ) : null}

            <div className="run-assessment-agreement-signature-block">
              {signatureDataUrl ? (
                <div className="run-assessment-agreement-signature-image-wrap">
                  <img
                    src={signatureDataUrl}
                    alt="Patient signature"
                    className="run-assessment-agreement-signature-image"
                  />
                </div>
              ) : null}
              <div className="run-assessment-agreement-signature-label">Patient Signature</div>
            </div>

            <div className="run-assessment-agreement-printed-name-block">
              <div className="run-assessment-agreement-printed-name-line">{fullName}</div>
              <div className="run-assessment-agreement-signature-label">Printed Name</div>
            </div>

            <div className="run-assessment-agreement-date-line">Date: {dateText}</div>
          </section>
        ) : null}
      </div>
    </div>
  );
};

const getPersistedAnswerPayload = (
  question,
  responseValue,
  {
    hasSubquestion = false,
    subquestionSelections = {},
    languageCode = "en",
  } = {}
) => {
  const availableOptions = normalizeOptions(question, languageCode);
  const addStoredOptionFields = (item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return item;

    const matchingOption = availableOptions.find((optionItem) =>
      matchesSelectableOption(item, optionItem)
    );

    return matchingOption
      ? {
          ...item,
          ...getStoredOptionFields(matchingOption, languageCode),
        }
      : item;
  };

  if (hasSubquestion && Array.isArray(responseValue)) {
    return responseValue.map((item) => {
      const storedItem = addStoredOptionFields(item);

      if (!shouldAllowSubquestionForAnswerValue(item?.value)) {
        if (!Object.prototype.hasOwnProperty.call(item ?? {}, "sub_answer")) {
          return {
            ...storedItem,
          };
        }

        const nextItem = {
          ...storedItem,
        };
        delete nextItem.sub_answer;
        return nextItem;
      }

      const itemIdentity = buildSelectableItemIdentity(item);
      const selectedSubAnswer =
        (itemIdentity && subquestionSelections?.[itemIdentity]) || item?.sub_answer || null;

      if (!selectedSubAnswer) {
        return {
          ...storedItem,
        };
      }

      return {
        ...storedItem,
        sub_answer: {
          value: selectedSubAnswer?.value ?? null,
          ...getStoredOptionFields(selectedSubAnswer, languageCode),
        },
      };
    });
  }

  if (normalizeQuestionType(question) !== "signature_agreement") {
    if (Array.isArray(responseValue)) {
      return responseValue.map(addStoredOptionFields);
    }

    if (responseValue && typeof responseValue === "object") {
      return addStoredOptionFields(responseValue);
    }

    return responseValue ?? null;
  }

  const normalizedResponse =
    responseValue && typeof responseValue === "object" && !Array.isArray(responseValue)
      ? responseValue
      : {};

  return {
    ...normalizedResponse,
  };
};

const getScaleResponseConfig = (questionType) => {
  const normalizedType = String(questionType ?? "").trim().toLowerCase();
  const match = normalizedType.match(/^scale_response_(-?\d+(?:\.\d+)?)_(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;

  const parsedMin = Number(match[1]);
  const parsedMax = Number(match[2]);

  if (!Number.isFinite(parsedMin) || !Number.isFinite(parsedMax)) return null;

  const min = Math.min(parsedMin, parsedMax);
  const max = Math.max(parsedMin, parsedMax);
  const isIntegerScale = Number.isInteger(min) && Number.isInteger(max);
  const midpoint = (min + max) / 2;
  const defaultValue = isIntegerScale
    ? Math.round(midpoint)
    : Number(midpoint.toFixed(2));

  return {
    min,
    max,
    step: isIntegerScale ? 1 : 0.1,
    defaultValue,
  };
};

const getQuestionOrder = (questionSection) => {
  const rawOrder =
    questionSection?.order ??
    questionSection?.question_order ??
    questionSection?.question_section_order ??
    questionSection?.question_section?.order ??
    questionSection?.question_section?.question_order ??
    null;

  const parsed = Number(rawOrder);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toBooleanRequired = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
};

const getQuestionSectionBoolean = (questionSection, fieldName) =>
  toBooleanRequired(
    questionSection?.[fieldName] ??
      questionSection?.question_section?.[fieldName] ??
      false
  );

const getQuestionSectionTypeLabel = (questionSection, fieldName) => {
  const rawValue =
    questionSection?.[fieldName] ??
    questionSection?.question_section?.[fieldName] ??
    null;

  if (!rawValue) return "";

  if (typeof rawValue === "object") {
    return String(rawValue?.description ?? rawValue?.label ?? rawValue?.name ?? "")
      .trim()
      .replace(/_/g, " ");
  }

  const text = String(rawValue).trim();
  return text ? `Type ${text}` : "";
};

const hasResponseValue = (value) => {
  if (value === null || value === undefined) return false;

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "object") {
    const objectValues = Object.values(value);
    if (objectValues.length === 0) return false;
    return objectValues.some((item) => hasResponseValue(item));
  }

  return true;
};

const toFiniteNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const shouldAllowSubquestionForAnswerValue = (value) => {
  const numericValue = toFiniteNumberOrNull(value);
  return numericValue !== null && numericValue > 0;
};

const toApiDecimalString = (value, fractionDigits = 2) => {
  const numericValue = toFiniteNumberOrNull(value);
  if (numericValue === null) return "0";

  const clampedFractionDigits = Math.max(0, Math.min(6, Number(fractionDigits) || 0));
  return numericValue
    .toFixed(clampedFractionDigits)
    .replace(/\.0+$/, "")
    .replace(/(\.\d*?)0+$/, "$1");
};

const shouldRetryPatientResponseCreateForAttemptField = (errorText) => {
  const normalizedError = String(errorText ?? "").toLowerCase();
  if (!normalizedError) return false;

  return [
    "attempt_id",
    "assessment_attempt_id",
    "patient_assessment_attempt_id",
    "patient_assessment_attempt",
    "unknown field",
    "this field is required",
  ].some((token) => normalizedError.includes(token));
};

const getScoreValueFromAnswer = (answerValue) => {
  if (!answerValue) return 0;

  const getSubAnswerIncrement = (item) => {
    const subAnswerValue = toFiniteNumberOrNull(item?.sub_answer?.value);
    if (subAnswerValue === null) return 0;
    return subAnswerValue * 0.1;
  };

  if (Array.isArray(answerValue)) {
    return answerValue.reduce((total, item) => {
      if (!item) return total;
      const parsed = toFiniteNumberOrNull(item?.value);
      return total + (parsed ?? 0) + getSubAnswerIncrement(item);
    }, 0);
  }

  if (typeof answerValue === "object") {
    const parsed = toFiniteNumberOrNull(answerValue?.value);
    return (parsed ?? 0) + getSubAnswerIncrement(answerValue);
  }

  return 0;
};

const getSelectedOptionOrder = (responseValue, availableOptions = []) => {
  if (responseValue === null || responseValue === undefined) return NaN;

  if (typeof responseValue === "object" && !Array.isArray(responseValue)) {
    const order = Number(responseValue?.order);
    if (Number.isFinite(order)) return order;
  }

  if (typeof responseValue === "number") {
    return Number.isFinite(responseValue) ? responseValue : NaN;
  }

  if (typeof responseValue === "string") {
    const trimmed = responseValue.trim();
    if (!trimmed) return NaN;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }

  const matchingOption = availableOptions.find((optionItem) =>
    matchesSelectableOption(responseValue, optionItem)
  );
  const matchingOrder = Number(matchingOption?.order);
  return Number.isFinite(matchingOrder) ? matchingOrder : NaN;
};

const getFlatQuestions = (sections) => {
  const sortedSections = [...(sections ?? [])].sort(
    (left, right) => Number(left.sectionOrder ?? 0) - Number(right.sectionOrder ?? 0)
  );

  return sortedSections.flatMap((section) => {
    const sortedQuestions = [...(section.questions ?? [])].sort((left, right) => {
      const leftOrder = getQuestionOrder(left);
      const rightOrder = getQuestionOrder(right);

      if (leftOrder === null && rightOrder === null) return 0;
      if (leftOrder === null) return 1;
      if (rightOrder === null) return -1;
      return leftOrder - rightOrder;
    });

    return sortedQuestions.map((questionSection, index) => ({
      assessmentSectionId: Number(section.assessment_section_id ?? section.assessmentSectionId ?? 0) || null,
      sectionId: section.section_id,
      sectionTitle: section.title,
      sectionOrder: Number(section.sectionOrder ?? 0),
      questionOrder: getQuestionOrder(questionSection),
      isRequired: toBooleanRequired(
        questionSection?.is_required ??
        questionSection?.question_section?.is_required ??
        false
      ),
      questionSectionId: Number(
        questionSection?.question_section_id ??
        questionSection?.question_section?.question_section_id ??
        0
      ),
      questionSection,
      question: questionSection?.question ?? {},
    }));
  });
};

let youTubeApiLoader = null;

const loadYouTubeIframeApi = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YouTube API is unavailable outside browser context."));
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (youTubeApiLoader) {
    return youTubeApiLoader;
  }

  youTubeApiLoader = new Promise((resolve) => {
    const existingScript = document.querySelector("script[src='https://www.youtube.com/iframe_api']");

    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReady === "function") {
        previousReady();
      }
      resolve(window.YT);
    };

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return youTubeApiLoader;
};

const hasPopulatedOptions = (options) => {
  if (!options) return false;
  if (Array.isArray(options)) return options.length > 0;
  if (typeof options === "object") return Object.keys(options).length > 0;
  return false;
};

const getLocalizedOptionKey = (languageCode = "en") => {
  const normalizedLanguageCode = String(languageCode ?? "en").trim().toLowerCase();
  const isEnglish = normalizedLanguageCode === "en" || normalizedLanguageCode === "english";
  return !isEnglish && normalizedLanguageCode ? `option_${normalizedLanguageCode}` : "";
};

const getLocalizedOptionLabel = (optionItem, languageCode = "en") => {
  const localizedOptionKey = getLocalizedOptionKey(languageCode);

  if (localizedOptionKey) {
    const localizedOption = optionItem?.[localizedOptionKey];

    if (localizedOption !== undefined && localizedOption !== null) {
      return String(localizedOption);
    }
  }

  return String(optionItem?.option ?? optionItem?.label ?? "");
};

const getLocalizedOptionMoreInfo = (optionItem, languageCode = "en") => {
  const localizedOptionKey = getLocalizedOptionKey(languageCode);

  if (localizedOptionKey) {
    const localizedMoreInfoKey = localizedOptionKey.replace("option_", "option_more_info_");
    const localizedMoreInfo = optionItem?.[localizedMoreInfoKey];

    if (localizedMoreInfo !== undefined && localizedMoreInfo !== null) {
      return String(localizedMoreInfo);
    }
  }

  return String(optionItem?.option_more_info ?? "");
};

const getStoredOptionFields = (optionItem, languageCode = "en") => {
  const storedFields = {
    option: String(optionItem?.default_option ?? optionItem?.option ?? optionItem?.label ?? ""),
  };
  const localizedOptionKey = getLocalizedOptionKey(languageCode);
  const localizedOption = localizedOptionKey ? optionItem?.[localizedOptionKey] : null;

  if (localizedOption !== undefined && localizedOption !== null) {
    storedFields[localizedOptionKey] = String(localizedOption);
  }

  return storedFields;
};

const normalizeOptionItems = (options, languageCode = "en") => {
  if (!options) return [];

  if (Array.isArray(options)) {
    return options
      .map((optionItem, index) => {
        if (optionItem && typeof optionItem === "object") {
          const defaultOption = String(optionItem?.option ?? optionItem?.label ?? "");
          const localizedOptionKey = getLocalizedOptionKey(languageCode);
          const localizedOption = localizedOptionKey ? optionItem?.[localizedOptionKey] : null;
          const normalizedOption = {
            order: Number(optionItem?.order ?? index + 1),
            option: getLocalizedOptionLabel(optionItem, languageCode),
            default_option: defaultOption,
            option_more_info: getLocalizedOptionMoreInfo(optionItem, languageCode),
            value: optionItem?.value ?? optionItem?.option ?? optionItem?.label ?? "",
          };

          if (localizedOption !== undefined && localizedOption !== null) {
            normalizedOption[localizedOptionKey] = String(localizedOption);
          }

          if (Object.prototype.hasOwnProperty.call(optionItem, "category")) {
            normalizedOption.category = optionItem.category;
          }

          return normalizedOption;
        }

        return {
          order: index + 1,
          option: String(optionItem ?? ""),
          option_more_info: "",
          value: optionItem ?? "",
        };
      })
      .filter((optionItem) => optionItem.option);
  }

  if (typeof options === "object") {
    const hasStructuredValues = Object.values(options).every(
      (value) => value && typeof value === "object" && ("option" in value || "label" in value)
    );

    if (hasStructuredValues) {
      return Object.values(options)
        .map((value, index) => {
          const defaultOption = String(value?.option ?? value?.label ?? "");
          const localizedOptionKey = getLocalizedOptionKey(languageCode);
          const localizedOption = localizedOptionKey ? value?.[localizedOptionKey] : null;
          const normalizedOption = {
            order: Number(value?.order ?? index + 1),
            option: getLocalizedOptionLabel(value, languageCode),
            default_option: defaultOption,
            option_more_info: getLocalizedOptionMoreInfo(value, languageCode),
            value: value?.value ?? value?.option ?? value?.label ?? "",
          };

          if (localizedOption !== undefined && localizedOption !== null) {
            normalizedOption[localizedOptionKey] = String(localizedOption);
          }

          if (Object.prototype.hasOwnProperty.call(value ?? {}, "category")) {
            normalizedOption.category = value.category;
          }

          return normalizedOption;
        })
        .filter((optionItem) => optionItem.option);
    }

    return Object.entries(options).map(([option, value], index) => ({
      order: index + 1,
      option,
      option_more_info: "",
      value,
    }));
  }

  return [];
};

const normalizeOptions = (question, languageCode = "en") => {
  const questionTypeOptions = question?.question_type?.options;
  const questionChoices = question?.choices;

  const options = hasPopulatedOptions(questionChoices)
    ? questionChoices
    : hasPopulatedOptions(questionTypeOptions)
      ? questionTypeOptions
      : null;

  return normalizeOptionItems(options, languageCode);
};

const getQuestionSectionValue = (questionSection, fieldName) =>
  questionSection?.[fieldName] ?? questionSection?.question_section?.[fieldName] ?? null;

const getQuestionSectionString = (questionSection, fieldName) =>
  String(getQuestionSectionValue(questionSection, fieldName) ?? "").trim();

const getLocalizedQuestionSectionString = (
  questionSection,
  fieldName,
  languageCode = "en"
) => {
  const normalizedLanguageCode = String(languageCode ?? "en").trim().toLowerCase();
  const isEnglish = normalizedLanguageCode === "en" || normalizedLanguageCode === "english";

  if (!isEnglish && normalizedLanguageCode) {
    const localizedValue = getQuestionSectionString(
      questionSection,
      `${fieldName}_${normalizedLanguageCode}`
    );

    if (localizedValue) {
      return localizedValue;
    }
  }

  return getQuestionSectionString(questionSection, fieldName);
};

const buildSelectableItemIdentity = (item) => {
  if (item && typeof item === "object") {
    const order = Number(item?.order);
    if (Number.isFinite(order)) {
      return `order:${order}`;
    }

    const optionLabel = String(item?.option ?? "").trim().toLowerCase();
    if (optionLabel) {
      return `option:${optionLabel}`;
    }

    const optionValue = String(item?.value ?? "").trim().toLowerCase();
    return optionValue ? `value:${optionValue}` : "";
  }

  const primitiveOrder = Number(item);
  if (Number.isFinite(primitiveOrder)) {
    return `order:${primitiveOrder}`;
  }

  const primitiveText = String(item ?? "").trim().toLowerCase();
  return primitiveText ? `option:${primitiveText}` : "";
};

const normalizeSelectableComparisonValues = (values) =>
  values
    .map((value) => String(value ?? "").trim().toLowerCase())
    .filter(Boolean);

const matchesSelectableOption = (item, optionItem) => {
  const itemIdentity = buildSelectableItemIdentity(item);
  const optionIdentity = buildSelectableItemIdentity(optionItem);
  if (Boolean(itemIdentity) && Boolean(optionIdentity) && itemIdentity === optionIdentity) {
    return true;
  }

  const itemIsObject = item && typeof item === "object" && !Array.isArray(item);
  const optionIsObject = optionItem && typeof optionItem === "object" && !Array.isArray(optionItem);
  const optionValues = normalizeSelectableComparisonValues([
    optionItem?.option,
    optionItem?.label,
    optionItem?.default_option,
    optionItem?.value,
  ]);

  if (!itemIsObject || !optionIsObject) {
    const itemValues = new Set(normalizeSelectableComparisonValues([item]));
    return optionValues.some((value) => itemValues.has(value));
  }

  const itemLabels = normalizeSelectableComparisonValues([
    item?.option,
    item?.label,
    item?.default_option,
  ]);
  if (itemLabels.length > 0) {
    const itemLabelSet = new Set(itemLabels);
    return optionValues.some((value) => itemLabelSet.has(value));
  }

  const itemValue = String(item?.value ?? "").trim().toLowerCase();
  return Boolean(itemValue) && Number.isNaN(Number(itemValue)) && optionValues.includes(itemValue);
};

const isExclusiveNoneMultiSelectOption = (item) => {
  const normalizedLabels = [item?.option, item?.label, item?.value]
    .map((value) => String(value ?? "").trim().toLowerCase())
    .filter(Boolean);

  return normalizedLabels.some(
    (label) => label === "none" || label === "none of these"
  );
};

const getSubquestionSelectionsFromResponseValue = (responseValue) => {
  if (!Array.isArray(responseValue)) return {};

  return responseValue.reduce((accumulator, item) => {
    const itemIdentity = buildSelectableItemIdentity(item);
    const subAnswer = item?.sub_answer;

    if (!itemIdentity || !subAnswer || typeof subAnswer !== "object") {
      return accumulator;
    }

    return {
      ...accumulator,
      [itemIdentity]: {
        option: String(subAnswer?.option ?? ""),
        value: subAnswer?.value ?? subAnswer?.option ?? "",
      },
    };
  }, {});
};

const buildSubquestionSelectionsByQuestionId = (responsesByQuestionId) =>
  Object.entries(responsesByQuestionId ?? {}).reduce((accumulator, [questionKey, responseValue]) => {
    const selections = getSubquestionSelectionsFromResponseValue(responseValue);
    if (!Object.keys(selections).length) return accumulator;

    return {
      ...accumulator,
      [questionKey]: selections,
    };
  }, {});

const formatPhoneInput = (rawValue) => {
  const digits = String(rawValue ?? "").replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const normalizeZipcodeInput = (rawValue) =>
  String(rawValue ?? "").replace(/\D/g, "").slice(0, 5);

const isValidZipcodeResponse = (value) => /^\d{5}$/.test(normalizeZipcodeInput(value));

const isValidEmailResponse = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? "").trim());

const normalizeVideoUrl = (rawUrl) => {
  const value = String(rawUrl ?? "").trim();
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) return value;
  if (/^\/\//.test(value)) return `https:${value}`;
  if (/^(www\.|youtu\.be\/|youtube\.com\/|vimeo\.com\/)/i.test(value)) {
    return `https://${value}`;
  }

  return value;
};

const isDirectVideoFileUrl = (urlValue) => {
  if (!urlValue) return false;
  return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(urlValue);
};

const toEmbeddableVideoUrl = (rawUrl, preferredLanguageCode = "en") => {
  const value = normalizeVideoUrl(rawUrl);
  if (!value) return "";

  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    const normalizedLanguageCode = String(preferredLanguageCode || "en")
      .trim()
      .toLowerCase();

    const buildYouTubeEmbedUrl = (videoId) => {
      const rawStart = parsed.searchParams.get("t") || parsed.searchParams.get("start");
      const startSeconds = Number.parseInt(String(rawStart ?? "").replace(/\D/g, ""), 10);
      const params = new URLSearchParams({
        rel: "0",
        modestbranding: "1",
        playsinline: "1",
        autoplay: "0",
        enablejsapi: "1",
      });

      ["hl", "cc_load_policy", "cc_lang_pref"].forEach((parameterName) => {
        const parameterValue = parsed.searchParams.get(parameterName);
        if (parameterValue !== null) {
          params.set(parameterName, parameterValue);
        }
      });

      if (normalizedLanguageCode && normalizedLanguageCode !== "en") {
        params.set("hl", normalizedLanguageCode);
        params.set("cc_load_policy", "1");
        params.set("cc_lang_pref", normalizedLanguageCode);
      }

      if (typeof window !== "undefined" && window.location?.origin) {
        params.set("origin", window.location.origin);
      }

      if (Number.isFinite(startSeconds) && startSeconds > 0) {
        params.set("start", String(startSeconds));
      }

      return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    };

    if (host.includes("youtu.be")) {
      const videoId = parsed.pathname.replace(/^\/+/, "").split("/")[0];
      if (!videoId) return value;
      return buildYouTubeEmbedUrl(videoId);
    }

    if (host.includes("youtube.com") || host.includes("youtube-nocookie.com")) {
      const pathParts = parsed.pathname.split("/").filter(Boolean);
      const watchId = parsed.searchParams.get("v");
      const shortsId = pathParts[0] === "shorts" ? pathParts[1] : null;
      const liveId = pathParts[0] === "live" ? pathParts[1] : null;
      const legacyId = pathParts[0] === "v" ? pathParts[1] : null;
      const embedId = pathParts[0] === "embed" ? pathParts[1] : null;
      const videoId = watchId || shortsId || liveId || legacyId || embedId;

      if (!videoId) return "";
      return buildYouTubeEmbedUrl(videoId);
    }

    if (host.includes("vimeo.com")) {
      const videoId = parsed.pathname.replace(/^\/+/, "").split("/")[0];
      if (!videoId) return "";
      return `https://player.vimeo.com/video/${videoId}`;
    }

    return "";
  } catch {
    return "";
  }
};

const SignatureDrawField = ({
  value,
  onChange,
  disabled = false,
  preferredLanguageCode = "en",
}) => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const isDrawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const activePointerIdRef = useRef(null);
  const lastPointRef = useRef({ x: 0, y: 0 });

  const normalizedValue = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
  const signatureDataUrl = String(normalizedValue.signature_data_url ?? "");
  const fullName = String(normalizedValue.full_name ?? "");
  const isSpanish = preferredLanguageCode === "es";

  const applyCanvasScale = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(280, Math.floor(wrapper.clientWidth));
    const height = 140;

    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const context = canvas.getContext("2d");
    if (!context) return;

    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = "#111111";
    context.lineWidth = 2.2;

    if (signatureDataUrl) {
      const image = new Image();
      image.onload = () => {
        context.drawImage(image, 0, 0, width, height);
      };
      image.src = signatureDataUrl;
      hasInkRef.current = true;
    } else {
      hasInkRef.current = false;
    }
  }, [signatureDataUrl]);

  useEffect(() => {
    applyCanvasScale();
  }, [applyCanvasScale]);

  useEffect(() => {
    const onResize = () => applyCanvasScale();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [applyCanvasScale]);

  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const emitSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    onChange({
      signature_data_url: hasInkRef.current ? canvas.toDataURL("image/png") : "",
      full_name: fullName,
    });
  };

  const startDraw = (event) => {
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    const point = getCanvasPoint(event);
    if (!point) return;

    isDrawingRef.current = true;
    activePointerIdRef.current = event.pointerId;
    lastPointRef.current = point;
    canvas.setPointerCapture?.(event.pointerId);

    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const drawMove = (event) => {
    if (!isDrawingRef.current) return;
    if (activePointerIdRef.current !== null && event.pointerId !== activePointerIdRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    const point = getCanvasPoint(event);
    if (!point) return;

    context.beginPath();
    context.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    context.lineTo(point.x, point.y);
    context.stroke();

    lastPointRef.current = point;
    hasInkRef.current = true;
  };

  const endDraw = (event) => {
    if (!isDrawingRef.current) return;
    if (activePointerIdRef.current !== null && event.pointerId !== activePointerIdRef.current) {
      return;
    }

    isDrawingRef.current = false;
    activePointerIdRef.current = null;
    emitSignature();
  };

  const clearSignature = () => {
    if (disabled) return;

    hasInkRef.current = false;
    onChange({
      signature_data_url: "",
      full_name: fullName,
    });
  };

  return (
    <div className={`run-assessment-signature-wrap ${disabled ? "is-disabled" : ""}`}>
      <div className="run-assessment-signature-header-row">
        <div className="run-assessment-signature-label">
          {isSpanish ? "Firme aquí" : "Sign Here"}
        </div>
        <button
          type="button"
          className="run-assessment-signature-clear-btn"
          onClick={clearSignature}
          disabled={disabled}
        >
          {isSpanish ? "Borrar firma" : "Clear Signature"}
        </button>
      </div>
      <div className="run-assessment-signature-canvas-wrap" ref={wrapperRef}>
        <canvas
          ref={canvasRef}
          className="run-assessment-signature-canvas"
          onPointerDown={startDraw}
          onPointerMove={drawMove}
          onPointerUp={endDraw}
          onPointerLeave={endDraw}
          onPointerCancel={endDraw}
        />
      </div>

      <label className="run-assessment-signature-name-label" htmlFor="signature-full-name-input">
        {isSpanish ? "Escriba su nombre completo aquí:" : "Type Your Full Name Here:"}
      </label>
      <input
        id="signature-full-name-input"
        type="text"
        value={fullName}
        disabled={disabled}
        onChange={(event) =>
          onChange({
            signature_data_url: signatureDataUrl,
            full_name: event.target.value,
          })
        }
        className="run-assessment-input"
      />
    </div>
  );
};

const AgreementSignatureField = ({
  value,
  onChange,
  agreementSource,
  documentName,
  questionText,
  patientData,
  documentTypeId,
  patientId,
  companyId,
  assessmentAttemptId,
  uploadRequestFn,
  onRegisterPdfExporter,
  emailRecipient,
  isRequired = false,
  questionTextClassName = "run-assessment-question-text",
  preferredLanguageCode = "en",
}) => {
  const scrollRef = useRef(null);
  const exportSurfaceRef = useRef(null);
  const hasUserScrolledRef = useRef(false);

  const normalizedValue = useMemo(
    () => (value && typeof value === "object" && !Array.isArray(value) ? value : {}),
    [value]
  );
  const signatureDataUrl = String(normalizedValue.signature_data_url ?? "");
  const fullName = String(normalizedValue.full_name ?? "");
  const hasViewedDocument = Boolean(normalizedValue.document_viewed);
  const displayDocumentName = String(documentName ?? "").trim() || "Agreement Document";
  const agreementDefinition = useMemo(
    () => normalizeStructuredAgreementSource(agreementSource),
    [agreementSource]
  );
  const hasAgreementContent = Boolean(agreementDefinition);
  const isSpanish = preferredLanguageCode === "es";
  const dateText = useMemo(() => formatDateMmDdYyyy(), []);
  const resolvedDocumentTypeId = Number(
    documentTypeId ?? getCaseInsensitiveValue(agreementDefinition, "document_type_id") ?? 0
  ) || null;

  const markDocumentViewedIfAtEnd = useCallback(() => {
    if (hasViewedDocument) return;
    if (!hasUserScrolledRef.current) return;

    const scrollNode = scrollRef.current;
    if (!scrollNode) return;

    const hasVerticalOverflow = scrollNode.scrollHeight > scrollNode.clientHeight + 8;
    if (!hasVerticalOverflow) return;

    const hasMovedFromTop = scrollNode.scrollTop > 0;
    if (!hasMovedFromTop) return;

    const reachedEnd = scrollNode.scrollTop + scrollNode.clientHeight >= scrollNode.scrollHeight - 8;
    if (!reachedEnd) return;

    onChange({
      ...normalizedValue,
      document_viewed: true,
      signature_data_url: signatureDataUrl,
      full_name: fullName,
    });
  }, [fullName, hasViewedDocument, normalizedValue, onChange, signatureDataUrl]);

  const markDocumentViewed = useCallback(() => {
    if (hasViewedDocument) return;

    onChange({
      ...normalizedValue,
      document_viewed: true,
      signature_data_url: signatureDataUrl,
      full_name: fullName,
    });
  }, [fullName, hasViewedDocument, normalizedValue, onChange, signatureDataUrl]);

  useEffect(() => {
    if (!hasAgreementContent) return;
    if (hasViewedDocument) return;

    const scrollNode = scrollRef.current;
    if (!scrollNode) return;

    const hasVerticalOverflow = scrollNode.scrollHeight > scrollNode.clientHeight + 8;
    if (!hasVerticalOverflow) {
      markDocumentViewed();
    }
  }, [hasAgreementContent, hasViewedDocument, markDocumentViewed]);

  const handleDocumentScroll = () => {
    hasUserScrolledRef.current = true;
    markDocumentViewedIfAtEnd();
  };

  useEffect(() => {
    if (!onRegisterPdfExporter) return undefined;

    onRegisterPdfExporter(async () => {
      const sourceNode = exportSurfaceRef.current;
      if (!sourceNode) return;

      const clonedNode = sourceNode.cloneNode(true);
      const wrapper = document.createElement("div");
      wrapper.style.position = "fixed";
      wrapper.style.left = "-10000px";
      wrapper.style.top = "0";
      wrapper.style.width = "900px";
      wrapper.style.padding = "24px";
      wrapper.style.background = "#ffffff";
      wrapper.style.zIndex = "-1";
      wrapper.appendChild(clonedNode);
      document.body.appendChild(wrapper);

      try {
        const canvas = await html2canvas(wrapper, {
          scale: 1.5,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          windowWidth: wrapper.scrollWidth,
          windowHeight: wrapper.scrollHeight,
        });

        const basePdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const pageWidth = basePdf.internal.pageSize.getWidth();
        const renderedHeightMm = (canvas.height / canvas.width) * pageWidth;
        const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: [pageWidth, renderedHeightMm] });
        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, renderedHeightMm);

        const resolvedPatientId = Number(patientId) || 1002;
        const resolvedCompanyId = Number(companyId) || 1000;
        const resolvedAttemptId = Number(assessmentAttemptId) || 46;
        const documentNameForUpload = createSafeGuidPdfFilename();

        const documentLinkRes = await uploadRequestFn(DOCUMENT_UPLOAD_GET_LINK_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patient_id: resolvedPatientId,
            company_id: resolvedCompanyId,
            document_type: resolvedDocumentTypeId,
            assessment_attempt_id: resolvedAttemptId,
            document_name: documentNameForUpload,
          }),
        });

        if (!documentLinkRes.ok) {
          const message = await documentLinkRes.text().catch(() => "");
          throw new Error(
            `Document link request failed (${documentLinkRes.status}) ${message}`
          );
        }

        const documentLinkPayload = await documentLinkRes.json().catch(() => null);
        const documentUrl = String(documentLinkPayload?.document_url ?? "").trim();
        const downloadUrl = String(documentLinkPayload?.download_url ?? "").trim();
        const patientDocumentId = Number(documentLinkPayload?.patient_document_id) || null;

        if (!documentUrl || !patientDocumentId) {
          throw new Error("Document upload link response is missing document_url or patient_document_id");
        }

        const pdfBlob = pdf.output("blob");
        let uploadRes;

        try {
          uploadRes = await fetch(documentUrl, {
            method: "PUT",
            headers: {
              "Content-Type": "application/pdf",
            },
            body: pdfBlob,
          });
        } catch (error) {
          throw new Error(
            getPresignedUploadErrorMessage(error, "Signature agreement upload")
          );
        }

        if (!uploadRes.ok) {
          const message = await uploadRes.text().catch(() => "");
          throw new Error(`PDF upload failed (${uploadRes.status}) ${message}`);
        }

        onChange((prevValue) => ({
          ...(prevValue && typeof prevValue === "object" && !Array.isArray(prevValue)
            ? prevValue
            : {}),
          document_viewed: true,
          signature_data_url: signatureDataUrl,
          full_name: fullName,
          patient_document_id: patientDocumentId,
        }));

        let agreementSent = false;

        try {
          await sendSignatureAgreementEmail({
            requestFn: uploadRequestFn,
            recipientEmail: emailRecipient,
            downloadUrl: downloadUrl || documentUrl,
            fileName: documentNameForUpload,
            firstName: patientData?.first_name,
            languageCode: preferredLanguageCode,
          });
          agreementSent = true;
        } catch (error) {
          console.error("Failed to send signature agreement email", error);
        }

        return {
          patientDocumentId,
          agreementSent,
        };
      } catch (error) {
        throw error;
      } finally {
        document.body.removeChild(wrapper);
      }
    });

    return () => {
      onRegisterPdfExporter(null);
    };
  }, [
    dateText,
    displayDocumentName,
    fullName,
    onRegisterPdfExporter,
    onChange,
    patientData,
    patientId,
    questionText,
    resolvedDocumentTypeId,
    companyId,
    emailRecipient,
    preferredLanguageCode,
    signatureDataUrl,
    assessmentAttemptId,
    uploadRequestFn,
  ]);

  return (
    <div className="run-assessment-agreement-wrap">
      <div className="run-assessment-agreement-doc-card">
        <div
          ref={scrollRef}
          className="run-assessment-agreement-doc-scroll"
          onScroll={handleDocumentScroll}
        >
          <div className="run-assessment-agreement-doc-pages">
            {!hasAgreementContent ? (
              <div className="run-assessment-video-empty">
                No agreement document content is configured for this question.
              </div>
            ) : (
              <AgreementDocumentContent
                agreementDefinition={agreementDefinition}
                displayDocumentName={displayDocumentName}
                patientData={patientData}
              />
            )}
          </div>
        </div>

        <div className="run-assessment-agreement-scroll-note">
          {hasViewedDocument
            ? isSpanish
              ? "La revisión del acuerdo ha finalizado. Ahora puede firmar a continuación."
              : "Agreement review complete. You can now sign below."
            : isSpanish
              ? "Desplácese hasta el final del acuerdo para desbloquear los campos de firma."
              : "Scroll to the end of the agreement to unlock the signature fields."}
        </div>
      </div>

      <p className={questionTextClassName}>
        {questionText || "No question text provided."}
        {isRequired && (
          <span className="run-assessment-required-marker" aria-label="required"> *</span>
        )}
      </p>

      <div className="run-assessment-agreement-export-surface" aria-hidden="true">
        <div ref={exportSurfaceRef} className="run-assessment-agreement-export-inner">
          {hasAgreementContent ? (
            <AgreementDocumentContent
              agreementDefinition={agreementDefinition}
              displayDocumentName={displayDocumentName}
              patientData={patientData}
              includeSignatureSection={true}
              questionText={questionText}
              signatureDataUrl={signatureDataUrl}
              fullName={fullName}
              dateText={dateText}
            />
          ) : null}
        </div>
      </div>

      <SignatureDrawField
        value={{ signature_data_url: signatureDataUrl, full_name: fullName }}
        disabled={!hasViewedDocument}
        preferredLanguageCode={preferredLanguageCode}
        onChange={(nextSignature) =>
          onChange({
            ...normalizedValue,
            document_viewed: hasViewedDocument,
            signature_data_url: String(nextSignature?.signature_data_url ?? ""),
            full_name: String(nextSignature?.full_name ?? ""),
          })
        }
      />
    </div>
  );
};

const getInitialResponse = (questionType) => {
  const scaleConfig = getScaleResponseConfig(questionType);
  if (scaleConfig) {
    return scaleConfig.defaultValue;
  }

  if (questionType === "multi_select" || questionType === "multiple_select") {
    return [];
  }
  if (questionType === "first_last_name_response") {
    return { first_name: "", last_name: "" };
  }
  if (questionType === "first_middle_last_name_response") {
    return { first_name: "", middle_name: "", last_name: "" };
  }
  if (questionType === "signature_draw") {
    return { signature_data_url: "", full_name: "" };
  }
  if (questionType === "signature_agreement") {
    return { document_viewed: false, signature_data_url: "", full_name: "" };
  }
  return "";
};

const getVideoSource = (rawUrl, preferredLanguageCode = "en") => {
  const normalizedUrl = normalizeVideoUrl(rawUrl);
  if (!normalizedUrl) {
    return { kind: "none", url: "" };
  }

  if (isDirectVideoFileUrl(normalizedUrl)) {
    return { kind: "native", url: normalizedUrl };
  }

  const embedUrl = toEmbeddableVideoUrl(normalizedUrl, preferredLanguageCode);
  if (embedUrl) {
    return { kind: "embed", url: embedUrl };
  }

  return { kind: "external", url: normalizedUrl };
};

const getYouTubeEmbedVideoId = (embedUrl) => {
  const match = String(embedUrl ?? "").match(
    /youtube(?:-nocookie)?\.com\/embed\/([^?&#/]+)/i
  );
  return match?.[1] ?? "";
};

const normalizeApiRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const getAttemptIdValue = (attempt) =>
  Number(
    attempt?.id ??
    attempt?.assessment_attempt_id ??
    attempt?.patient_assessment_attempt_id ??
    attempt?.attempt_id ??
    0
  ) || 0;

const getAttemptPatientIdValue = (attempt) =>
  Number(attempt?.patient?.patient_id ?? attempt?.patient_id ?? 0) || 0;

const getAttemptAssessmentIdValue = (attempt) =>
  Number(attempt?.assessment?.assessment_id ?? attempt?.assessment_id ?? 0) || 0;

const getAttemptQuestionIdValue = (attempt) =>
  Number(attempt?.current_question?.question_id ?? attempt?.current_question_id ?? 0) || 0;

const getAttemptSectionIdValue = (attempt) =>
  Number(
    attempt?.current_section?.assessment_section_id ??
    attempt?.current_section?.id ??
    attempt?.current_section_id ??
    0
  ) || 0;

const getResponseRecordId = (responseItem) =>
  Number(responseItem?.id ?? responseItem?.patient_response_id ?? 0) || 0;

const getResponseQuestionId = (responseItem) =>
  Number(
    responseItem?.question?.question_id ??
    responseItem?.question_id ??
    responseItem?.question ??
    0
  ) || 0;

const getResponseAttemptId = (responseItem) =>
  Number(
    responseItem?.attempt?.id ??
      responseItem?.attempt_id ??
      responseItem?.assessment_attempt_id ??
      responseItem?.patient_assessment_attempt_id ??
      responseItem?.patient_assessment_attempt ??
      responseItem?.attempt ??
      0
  ) || 0;

const normalizeStoredAnswerValue = (value) => {
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return value;

  const looksJson =
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"));

  if (!looksJson) return value;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const unwrapAssessmentResponseRow = (responseItem) =>
  responseItem?.patient_response ??
  responseItem?.response ??
  responseItem?.patient_response_history ??
  responseItem?.history_record ??
  responseItem;

const getAssessmentResponseQuestionId = (responseItem) =>
  Number(
    unwrapAssessmentResponseRow(responseItem)?.question?.question_id ??
      unwrapAssessmentResponseRow(responseItem)?.question_id ??
      unwrapAssessmentResponseRow(responseItem)?.question ??
      0
  ) || 0;

const getAssessmentResponseAttemptId = (responseItem) =>
  Number(
    unwrapAssessmentResponseRow(responseItem)?.attempt?.id ??
      unwrapAssessmentResponseRow(responseItem)?.attempt_id ??
      unwrapAssessmentResponseRow(responseItem)?.assessment_attempt_id ??
      unwrapAssessmentResponseRow(responseItem)?.patient_assessment_attempt_id ??
      unwrapAssessmentResponseRow(responseItem)?.patient_assessment_attempt ??
      unwrapAssessmentResponseRow(responseItem)?.attempt ??
      0
  ) || 0;

const getAssessmentResponseCalcValue = (responseItem) => {
  const resolvedResponse = unwrapAssessmentResponseRow(responseItem);
  const rawCalcValue =
    resolvedResponse?.calc_value ??
    resolvedResponse?.calcValue ??
    resolvedResponse?.calculated_value ??
    resolvedResponse?.calculatedValue ??
    0;

  const numericCalcValue = Number(rawCalcValue);
  return Number.isFinite(numericCalcValue) ? numericCalcValue : 0;
};

const getAssessmentResponseAnswerValue = (responseItem) =>
  normalizeStoredAnswerValue(
    unwrapAssessmentResponseRow(responseItem)?.answer_value ??
      unwrapAssessmentResponseRow(responseItem)?.answerValue ??
      unwrapAssessmentResponseRow(responseItem)?.response_value ??
      unwrapAssessmentResponseRow(responseItem)?.responseValue ??
      unwrapAssessmentResponseRow(responseItem)?.answer ??
      null
  );

const collectPositiveOptionLabels = (answerValue) => {
  if (Array.isArray(answerValue)) {
    return answerValue.flatMap((entry) => collectPositiveOptionLabels(entry));
  }

  if (answerValue && typeof answerValue === "object") {
    const optionLabel = String(answerValue?.option ?? "").trim();
    const numericValue = toFiniteNumberOrNull(answerValue?.value);

    if (optionLabel && numericValue !== null && numericValue >= 1) {
      return [optionLabel];
    }

    return Object.values(answerValue).flatMap((entry) => collectPositiveOptionLabels(entry));
  }

  return [];
};

const computeAssessment1001AttemptMetrics = (responseRows) => {
  const getResponseForQuestionId = (questionId) =>
    (responseRows || []).find((responseItem) => getAssessmentResponseQuestionId(responseItem) === questionId) || null;

  const sumCalcValues = (questionIds) =>
    questionIds.reduce(
      (total, questionId) => total + getAssessmentResponseCalcValue(getResponseForQuestionId(questionId)),
      0
    );

  const locSelfScore = sumCalcValues([18, 19]);
  const locWitnessScore = getAssessmentResponseCalcValue(getResponseForQuestionId(20));
  const aocSelfScore = sumCalcValues([22, 23]);
  const aocWitnessScore = getAssessmentResponseCalcValue(getResponseForQuestionId(24));
  const ptaSelfScore = sumCalcValues([26, 27]);
  const ptaWitnessScore = getAssessmentResponseCalcValue(getResponseForQuestionId(28));
  const classCalcValue =
    locSelfScore +
    locWitnessScore +
    aocSelfScore +
    aocWitnessScore +
    ptaSelfScore +
    ptaWitnessScore;

  const baselinePositiveOptionSet = new Set(
    collectPositiveOptionLabels(getAssessmentResponseAnswerValue(getResponseForQuestionId(31)))
  );
  const acutePositiveOptions = Array.from(
    new Set(collectPositiveOptionLabels(getAssessmentResponseAnswerValue(getResponseForQuestionId(33))))
  );
  const currentPositiveOptions = Array.from(
    new Set(collectPositiveOptionLabels(getAssessmentResponseAnswerValue(getResponseForQuestionId(35))))
  );
  const symptomProgressionOptionSet = new Set(
    acutePositiveOptions.filter((optionLabel) => !baselinePositiveOptionSet.has(optionLabel))
  );

  currentPositiveOptions.forEach((optionLabel) => {
    symptomProgressionOptionSet.add(optionLabel);
  });

  return {
    loc_self_score: locSelfScore,
    loc_witness_score: locWitnessScore,
    aoc_self_score: aocSelfScore,
    aoc_witness_score: aocWitnessScore,
    pta_self_score: ptaSelfScore,
    pta_witness_score: ptaWitnessScore,
    class_calc_value: classCalcValue,
    symptom_progression_score: symptomProgressionOptionSet.size,
  };
};

const resolveAttemptClassification = (classificationRows, classCalcValue) => {
  const numericClassCalcValue = Number(classCalcValue);
  if (!Number.isFinite(numericClassCalcValue)) return null;

  const matchingClassification = (classificationRows || []).find((row) => {
    const valueFrom = Number(row?.value_from);
    const valueTo = Number(row?.value_to);

    if (!Number.isFinite(valueFrom) || !Number.isFinite(valueTo)) return false;

    return numericClassCalcValue >= valueFrom && numericClassCalcValue <= valueTo;
  });

  const classificationText = String(matchingClassification?.classification ?? "").trim();
  return classificationText || null;
};

const getRulePriorityValue = (rule) => {
  const parsed = Number(rule?.priority);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

const getSourceQuestionIdFromRule = (rule) =>
  Number(rule?.source_question?.question_id ?? rule?.source_question_id ?? 0) || 0;

const getTargetSectionIdFromRule = (rule) =>
  Number(rule?.target_section?.section_id ?? rule?.target_section_id ?? 0) || 0;

const getRuleMatchValues = (rule) => {
  const raw = rule?.match_value;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") return [raw];
  return [];
};

const normalizeComparableValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const text = String(value).trim();
  if (!text) return null;

  const numeric = Number(text);
  if (Number.isFinite(numeric)) return numeric;

  return text;
};

const collectResponseComparableValues = (responseValue) => {
  if (responseValue === null || responseValue === undefined) return [];

  if (Array.isArray(responseValue)) {
    const values = responseValue.flatMap((item) => collectResponseComparableValues(item));
    return Array.from(new Set(values.map((v) => String(v)))).map((v) => {
      const num = Number(v);
      return Number.isFinite(num) && String(num) === v ? num : v;
    });
  }

  if (typeof responseValue === "object") {
    const values = [];
    const valueCandidate = normalizeComparableValue(responseValue?.value);
    if (valueCandidate !== null) values.push(valueCandidate);

    const orderCandidate = normalizeComparableValue(responseValue?.order);
    if (orderCandidate !== null) values.push(orderCandidate);

    return values;
  }

  const normalized = normalizeComparableValue(responseValue);
  return normalized === null ? [] : [normalized];
};

const doesRuleMatchResponseValue = (rule, responseValue) => {
  const responseValues = collectResponseComparableValues(responseValue);
  if (!responseValues.length) return false;

  const ruleValues = getRuleMatchValues(rule)
    .map((entry) => normalizeComparableValue(entry?.value))
    .filter((entry) => entry !== null);

  if (!ruleValues.length) return false;

  return ruleValues.some((ruleValue) =>
    responseValues.some((responseCandidate) => responseCandidate === ruleValue)
  );
};

export default function RunAssessment({
  isOpen,
  onClose,
  onSubmitComplete,
  assessmentName,
  sections,
  shouldPersistAssessmentResponses = true,
  startQuestionSectionId = null,
  startQuestionId = null,
  forceConditionalSourceQuestionId = null,
  forceConditionalTargetSectionId = null,
  patientId = null,
  patient = null,
  assessmentId = null,
  patientEventId = null,
  attemptIdOverride = null,
  companyId = null,
  generateReportOnSubmit = false,
  skippedQuestionSectionIds = [],
  preferredLanguageCode = "en",
  prefillData = null,
  showMetadataToggle = true,
  signatureAgreementEmailRecipient = null,
  requestFn = apiRequest,
}) {
  const normalizedPreferredLanguageCode = String(preferredLanguageCode || "en")
    .trim()
    .toLowerCase();
  const isSpanish = normalizedPreferredLanguageCode === "es";
  const skippedQuestionSectionIdSet = useMemo(
    () => new Set(
      (Array.isArray(skippedQuestionSectionIds) ? skippedQuestionSectionIds : [])
        .map((questionSectionId) => Number(questionSectionId))
        .filter((questionSectionId) => Number.isFinite(questionSectionId) && questionSectionId > 0)
    ),
    [skippedQuestionSectionIds]
  );
  const questions = useMemo(
    () => getFlatQuestions(sections).filter(
      (item) => !skippedQuestionSectionIdSet.has(Number(item?.questionSectionId))
    ),
    [sections, skippedQuestionSectionIdSet]
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [showMetadata, setShowMetadata] = useState(false);
  const [shellEnterClass, setShellEnterClass] = useState("");
  const [isAnimatingShell, setIsAnimatingShell] = useState(false);
  const [videoCompletionByQuestionId, setVideoCompletionByQuestionId] = useState({});
  const [youtubeEmbedFallbackByQuestionId, setYoutubeEmbedFallbackByQuestionId] = useState({});
  const shellRef = useRef(null);
  const youtubeContainerRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  const youtubeMountNodeRef = useRef(null);
  const shellAnimationDurationMs = 319;
  const selectedCompany = getSelectedCompany();
  const [resolvedPatient, setResolvedPatient] = useState(
    patient && typeof patient === "object" ? patient : null
  );
  const [attemptId, setAttemptId] = useState(null);
  const [isAttemptReady, setIsAttemptReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureUploadError, setSignatureUploadError] = useState("");
  const [flowRules, setFlowRules] = useState([]);
  const [conditionalQuestionSectionsBySectionId, setConditionalQuestionSectionsBySectionId] = useState({});
  const [isConditionalFlowDataLoading, setIsConditionalFlowDataLoading] = useState(false);
  const [isResolvingRequestedStart, setIsResolvingRequestedStart] = useState(false);
  const [subquestionSelectionsByQuestionId, setSubquestionSelectionsByQuestionId] = useState({});
  const [activeSubquestionPopup, setActiveSubquestionPopup] = useState(null);
  const [activeOptionMoreInfoCard, setActiveOptionMoreInfoCard] = useState(null);
  const [isAutoAdvancingHiddenQuestion, setIsAutoAdvancingHiddenQuestion] = useState(false);
  const currentSignaturePdfExporterRef = useRef(null);
  const responsesRef = useRef({});
  const subquestionSelectionsByQuestionIdRef = useRef({});
  const responseIdByQuestionIdRef = useRef({});
  const appliedStartRequestRef = useRef(null);
  const optionMoreInfoTimerRef = useRef(null);
  const autoAdvanceHiddenQuestionKeyRef = useRef("");

  const clearOptionMoreInfoTimer = () => {
    if (optionMoreInfoTimerRef.current !== null) {
      window.clearTimeout(optionMoreInfoTimerRef.current);
      optionMoreInfoTimerRef.current = null;
    }
  };

  const numericPatientId = Number(patientId);
  const numericCompanyId = Number(
    companyId ??
    selectedCompany?.company_id ??
    selectedCompany?.id ??
    selectedCompany?.company?.company_id ??
    0
  );
  const numericAssessmentId = Number(assessmentId);
  const numericPatientEventId = Number(patientEventId);
  const numericAttemptIdOverride = Number(attemptIdOverride);
  const hasValidAttemptOverride =
    Number.isFinite(numericAttemptIdOverride) && numericAttemptIdOverride > 0;
  const hasAttemptContext =
    hasValidAttemptOverride || (
      Number.isFinite(numericPatientId) && numericPatientId > 0 &&
      Number.isFinite(numericAssessmentId) && numericAssessmentId > 0
    );
  const canPersistAttempts = shouldPersistAssessmentResponses && hasAttemptContext;

  useEffect(() => {
    setResolvedPatient(patient && typeof patient === "object" ? patient : null);
  }, [patient]);

  useEffect(() => {
    if (!isOpen) return;
    if (patient && typeof patient === "object") return;
    if (!Number.isFinite(numericPatientId) || numericPatientId <= 0) return;

    let cancelled = false;

    const fetchPatient = async () => {
      try {
        const response = await apiRequest(`${PATIENTS_API}${numericPatientId}/`);
        if (!response.ok) return;

        const patientPayload = await response.json().catch(() => null);
        if (!cancelled && patientPayload && typeof patientPayload === "object") {
          setResolvedPatient(patientPayload);
        }
      } catch {
        // Best-effort hydration for agreement placeholder interpolation.
      }
    };

    fetchPatient();

    return () => {
      cancelled = true;
    };
  }, [isOpen, numericPatientId, patient]);

  useEffect(() => {
    return () => {
      clearOptionMoreInfoTimer();
    };
  }, []);

  const getQuestionIdForItem = (item) =>
    Number(item?.question?.question_id ?? item?.questionSection?.question_id ?? 0) || 0;

  const getSectionIdForItem = (item) =>
    Number(item?.assessmentSectionId ?? item?.sectionId ?? 0) || 0;

  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  const questionsInitializationKey = questions
    .map((item, index) => [
      Number(item?.questionSectionId ?? 0) || 0,
      getQuestionIdForItem(item),
      getSectionIdForItem(item),
      Number(item?.questionOrder ?? index + 1),
    ].join(":"))
    .join("|");

  const setResponsesWithRef = (valueOrUpdater) => {
    const nextResponses =
      typeof valueOrUpdater === "function"
        ? valueOrUpdater(responsesRef.current)
        : valueOrUpdater;

    responsesRef.current = nextResponses;
    setResponses(nextResponses);
    return nextResponses;
  };

  const setSubquestionSelectionsWithRef = (valueOrUpdater) => {
    const nextSelections =
      typeof valueOrUpdater === "function"
        ? valueOrUpdater(subquestionSelectionsByQuestionIdRef.current)
        : valueOrUpdater;

    subquestionSelectionsByQuestionIdRef.current = nextSelections;
    setSubquestionSelectionsByQuestionId(nextSelections);
    return nextSelections;
  };

  useEffect(() => {
    if (!isOpen) {
      setFlowRules([]);
      setConditionalQuestionSectionsBySectionId({});
      setIsConditionalFlowDataLoading(false);
      return;
    }

    const resolvedAssessmentId = Number(assessmentId);
    if (!Number.isFinite(resolvedAssessmentId) || resolvedAssessmentId <= 0) {
      setFlowRules([]);
      setConditionalQuestionSectionsBySectionId({});
      return;
    }

    let cancelled = false;

    const loadConditionalFlowData = async () => {
      if (!cancelled) {
        setIsConditionalFlowDataLoading(true);
      }

      try {
        const forcedTargetSectionId = Number(forceConditionalTargetSectionId);
        const hasForcedTargetSectionId =
          Number.isFinite(forcedTargetSectionId) && forcedTargetSectionId > 0;

        const rulesRes = await requestFn(
          `${API_BASE}/api/question-flow-rules/?assessment=${resolvedAssessmentId}`
        );

        if (!rulesRes.ok) {
          if (!cancelled) {
            setFlowRules([]);
            setConditionalQuestionSectionsBySectionId({});
            setIsConditionalFlowDataLoading(false);
          }
          return;
        }

        const rulesPayload = await rulesRes.json();
        const activeRules = (Array.isArray(rulesPayload) ? rulesPayload : []).filter(
          (rule) => rule?.is_active ?? true
        );

        const targetSectionIds = Array.from(
          new Set(
            activeRules
              .map((rule) => getTargetSectionIdFromRule(rule))
              .filter((sectionId) => Number.isFinite(sectionId) && sectionId > 0)
          )
        );

        if (hasForcedTargetSectionId && !targetSectionIds.includes(forcedTargetSectionId)) {
          targetSectionIds.push(forcedTargetSectionId);
        }

        if (!cancelled) {
          setFlowRules(activeRules);
        }

        if (!targetSectionIds.length) {
          if (!cancelled) {
            setConditionalQuestionSectionsBySectionId({});
            setIsConditionalFlowDataLoading(false);
          }
          return;
        }

        const questionSectionsRes = await requestFn(`${API_BASE}/api/question-sections/`);
        if (!questionSectionsRes.ok) {
          if (!cancelled) {
            setConditionalQuestionSectionsBySectionId({});
            setIsConditionalFlowDataLoading(false);
          }
          return;
        }

        const questionSectionsPayload = await questionSectionsRes.json();
        const allQuestionSections = normalizeApiRows(questionSectionsPayload);
        const targetSectionIdSet = new Set(targetSectionIds);
        const grouped = {};

        allQuestionSections.forEach((questionSection) => {
          const sectionId = Number(
            questionSection?.section_id ??
              questionSection?.section?.section_id ??
              0
          );

          if (!Number.isFinite(sectionId) || !targetSectionIdSet.has(sectionId)) return;

          if (!grouped[sectionId]) grouped[sectionId] = [];
          grouped[sectionId].push(questionSection);
        });

        Object.keys(grouped).forEach((sectionId) => {
          grouped[sectionId] = grouped[sectionId].slice().sort((left, right) => {
            const leftOrder = getQuestionOrder(left) ?? Number.MAX_SAFE_INTEGER;
            const rightOrder = getQuestionOrder(right) ?? Number.MAX_SAFE_INTEGER;
            return leftOrder - rightOrder;
          });
        });

        if (!cancelled) {
          setConditionalQuestionSectionsBySectionId(grouped);
          setIsConditionalFlowDataLoading(false);
        }
      } catch (error) {
        console.error("Failed to load conditional flow data", error);
        if (!cancelled) {
          setFlowRules([]);
          setConditionalQuestionSectionsBySectionId({});
          setIsConditionalFlowDataLoading(false);
        }
      }
    };

    loadConditionalFlowData();

    return () => {
      cancelled = true;
    };
  }, [isOpen, assessmentId, forceConditionalTargetSectionId, requestFn]);

  useEffect(() => {
    if (!isOpen) {
      setIsResolvingRequestedStart(false);
      return;
    }

    const requestedSectionId = Number(startQuestionSectionId);
    const requestedQuestionId = Number(startQuestionId);
    const forcedSourceQuestionId = Number(forceConditionalSourceQuestionId);
    const hasRequestedSectionId = Number.isFinite(requestedSectionId) && requestedSectionId > 0;
    const hasRequestedQuestionId = Number.isFinite(requestedQuestionId) && requestedQuestionId > 0;
    const hasForcedSourceQuestionId =
      Number.isFinite(forcedSourceQuestionId) && forcedSourceQuestionId > 0;

    setIsResolvingRequestedStart(
      hasRequestedSectionId || hasRequestedQuestionId || hasForcedSourceQuestionId
    );
  }, [
    isOpen,
    startQuestionSectionId,
    startQuestionId,
    forceConditionalSourceQuestionId,
  ]);

  const prefillContext = useMemo(() => {
    try {
      const empty = {
        dobYmd: null,
        age: null,
        eventDateYmd: null,
        eventDescription: null,
        firstName: null,
        lastName: null,
        referralCompanyName: null,
      };

      if (!prefillData || typeof prefillData !== "object") {
        return empty;
      }

      const dobParts = parseDateOnlyYmd(
        prefillData?.dob ??
          prefillData?.patientDob ??
          prefillData?.patient_dob ??
          null
      );
      const age = calculateAgeFromDob(dobParts);

      const eventDateParts = parseDateOnlyYmd(
        prefillData?.event_date ??
          prefillData?.eventDate ??
          prefillData?.patient_event_date ??
          null
      );

      const eventDescriptionRaw =
        prefillData?.event ??
          prefillData?.eventDescription ??
          prefillData?.patient_event_description ??
          null;

      const eventDescription =
        typeof eventDescriptionRaw === "string" ? eventDescriptionRaw.trim() : "";

      const firstNameRaw =
        prefillData?.first_name ??
          prefillData?.firstName ??
          prefillData?.patient_first_name ??
          null;
      const lastNameRaw =
        prefillData?.last_name ??
          prefillData?.lastName ??
          prefillData?.patient_last_name ??
          null;

      const firstName = typeof firstNameRaw === "string" ? firstNameRaw.trim() : "";
      const lastName = typeof lastNameRaw === "string" ? lastNameRaw.trim() : "";

      const referralCompanyNameRaw =
        prefillData?.referral_company_name ??
          prefillData?.referralCompanyName ??
          prefillData?.company_name ??
          prefillData?.companyName ??
          null;
      const referralCompanyName =
        typeof referralCompanyNameRaw === "string" ? referralCompanyNameRaw.trim() : "";

      return {
        dobYmd: dobParts?.ymd ?? null,
        age,
        eventDateYmd: eventDateParts?.ymd ?? null,
        eventDescription: eventDescription ? eventDescription : null,
        firstName: firstName ? firstName : null,
        lastName: lastName ? lastName : null,
        referralCompanyName: referralCompanyName ? referralCompanyName : null,
      };
    } catch {
      return {
        dobYmd: null,
        age: null,
        eventDateYmd: null,
        eventDescription: null,
        firstName: null,
        lastName: null,
        referralCompanyName: null,
      };
    }
  }, [prefillData]);

  const patientTemplateData = useMemo(
    () => buildPatientTemplateData(resolvedPatient, prefillContext),
    [prefillContext, resolvedPatient]
  );
  const resolvedSignatureAgreementEmailRecipient = useMemo(() => {
    const overrideEmail = String(signatureAgreementEmailRecipient ?? "").trim();
    if (overrideEmail) {
      return overrideEmail;
    }

    return String(resolvedPatient?.email ?? patient?.email ?? "").trim();
  }, [patient?.email, resolvedPatient?.email, signatureAgreementEmailRecipient]);

  useEffect(() => {
    if (!isOpen) {
      setIsAttemptReady(false);
      return;
    }

    let cancelled = false;
    const initializationQuestions = questionsRef.current;

    const resolveStartIndex = (attemptData) => {
      const requestedStartId = Number(startQuestionSectionId);
      if (Number.isFinite(requestedStartId) && requestedStartId > 0) {
        const requestedIndex = initializationQuestions.findIndex(
          (item) => Number(item.questionSectionId) === requestedStartId
        );

        if (requestedIndex >= 0) {
          return requestedIndex;
        }
      }

      const attemptQuestionId = getAttemptQuestionIdValue(attemptData);
      if (attemptQuestionId > 0) {
        const questionIndex = initializationQuestions.findIndex(
          (item) => getQuestionIdForItem(item) === attemptQuestionId
        );
        if (questionIndex >= 0) return questionIndex;
      }

      const attemptSectionId = getAttemptSectionIdValue(attemptData);
      if (attemptSectionId > 0) {
        const sectionIndex = initializationQuestions.findIndex(
          (item) => getSectionIdForItem(item) === attemptSectionId
        );
        if (sectionIndex >= 0) return sectionIndex;
      }

      return 0;
    };

    const initializeRunState = async () => {
      const initStartedAt = Date.now();
      const perfMarks = {};
      const mark = (label) => {
        perfMarks[label] = Date.now();
      };
      const logInitPerf = (statusLabel, extra = {}) => {
        if (!PERF_DEBUG) return;
        const attemptResolveMs =
          perfMarks.attemptResolvedAt && perfMarks.attemptResolveStartedAt
            ? perfMarks.attemptResolvedAt - perfMarks.attemptResolveStartedAt
            : 0;
        const responseLoadMs =
          perfMarks.responsesLoadedAt && perfMarks.responseLoadStartedAt
            ? perfMarks.responsesLoadedAt - perfMarks.responseLoadStartedAt
            : 0;
        const totalMs = Date.now() - initStartedAt;

        console.info("[RunAssessment Perf]", {
          status: statusLabel,
          attemptResolveMs,
          responseLoadMs,
          totalMs,
          ...extra,
        });
      };

      const getAttemptIdFromResponseItem = (responseItem) => Number(
        responseItem?.attempt?.id ??
        responseItem?.attempt_id ??
        responseItem?.assessment_attempt_id ??
        responseItem?.patient_assessment_attempt_id ??
        responseItem?.patient_assessment_attempt ??
        responseItem?.attempt ??
        0
      );

      const loadResponsesForAttempt = async (resolvedAttemptId) => {
        const responseQueryPaths = [
          `${PATIENT_RESPONSES_HISTORY_API}?assessment_attempt_id=${resolvedAttemptId}`,
          `${PATIENT_RESPONSES_API}?attempt_id=${resolvedAttemptId}`,
          `${PATIENT_RESPONSES_API}?assessment_attempt_id=${resolvedAttemptId}`,
          `${PATIENT_RESPONSES_API}?attempt=${resolvedAttemptId}`,
          `${PATIENT_RESPONSES_API}?patient_assessment_attempt_id=${resolvedAttemptId}`,
        ];

        for (const responsePath of responseQueryPaths) {
          try {
            const scopedRes = await requestFn(responsePath);
            if (!scopedRes.ok) continue;

            const scopedPayload = await scopedRes.json();
            const scopedRows = normalizeApiRows(scopedPayload);
            const scopedMatches = scopedRows.filter(
              (responseItem) => getAttemptIdFromResponseItem(responseItem) === resolvedAttemptId
            );
            const rowsWithKnownAttemptId = scopedRows.filter(
              (responseItem) => getAttemptIdFromResponseItem(responseItem) > 0
            );
            const hasMismatchedAttemptIds = rowsWithKnownAttemptId.some(
              (responseItem) => getAttemptIdFromResponseItem(responseItem) !== resolvedAttemptId
            );

            if (scopedMatches.length > 0) {
              return scopedMatches;
            }

            // Some filtered endpoints omit attempt metadata from each response row.
            // When no known attempt ids contradict the target attempt, trust scoped results.
            if (scopedRows.length > 0 && !hasMismatchedAttemptIds) {
              return scopedRows;
            }

            continue;
          } catch {
            // Try the next candidate path.
          }
        }

        const responsesRes = await requestFn(PATIENT_RESPONSES_API);
        const responsesPayload = await responsesRes.json();
        const allResponses = normalizeApiRows(responsesPayload);
        return allResponses.filter(
          (responseItem) => getAttemptIdFromResponseItem(responseItem) === resolvedAttemptId
        );
      };

      const mapAttemptResponsesToState = (attemptResponses) => {
        const loadedResponses = {};
        const loadedResponseIdByQuestionId = {};

        attemptResponses.forEach((responseItem) => {
          const resolvedQuestionId = getResponseQuestionId(responseItem);
          if (!resolvedQuestionId) return;

          const resolvedAnswerValue =
            responseItem?.answer_value ??
            responseItem?.answerValue ??
            responseItem?.response_value ??
            responseItem?.responseValue ??
            responseItem?.answer ??
            null;

          loadedResponses[resolvedQuestionId] = normalizeStoredAnswerValue(resolvedAnswerValue);
          const responseRecordId = getResponseRecordId(responseItem);
          if (responseRecordId) {
            loadedResponseIdByQuestionId[resolvedQuestionId] = responseRecordId;
          }
        });

        return {
          loadedResponses,
          loadedResponseIdByQuestionId,
        };
      };

      if (!initializationQuestions.length) {
        setIsAttemptReady(true);
        logInitPerf("no-questions");
        return;
      }

      if (!canPersistAttempts) {
        const basicStartIndex = resolveStartIndex(null);
        if (!cancelled) {
          setCurrentIndex(Math.max(0, basicStartIndex));
          setAttemptId(null);
          responseIdByQuestionIdRef.current = {};
          setResponsesWithRef({});
          setSubquestionSelectionsWithRef({});
          setIsAttemptReady(true);
        }
        logInitPerf("no-persist");
        return;
      }

      setIsAttemptReady(false);

      try {
        let activeAttempt = null;
        mark("attemptResolveStartedAt");

        if (Number.isFinite(numericAttemptIdOverride) && numericAttemptIdOverride > 0) {
          let attemptRes = await requestFn(
            `${PATIENT_ASSESSMENT_ATTEMPTS_API}${numericAttemptIdOverride}/`
          );

          if (!attemptRes.ok && attemptRes.status === 404) {
            attemptRes = await requestFn(
              `${PATIENT_ASSESSMENT_ATTEMPTS_API}${numericAttemptIdOverride}`
            );
          }

          if (attemptRes.ok) {
            const attemptPayload = await attemptRes.json();
            const matchesContext =
              getAttemptPatientIdValue(attemptPayload) === numericPatientId &&
              getAttemptAssessmentIdValue(attemptPayload) === numericAssessmentId;

            if (matchesContext) {
              activeAttempt = attemptPayload;
            } else {
              throw new Error(
                "Resolved attempt id does not match patient/assessment context for this session."
              );
            }
          } else {
            throw new Error(
              `Unable to load attempt ${numericAttemptIdOverride} for this session (status ${attemptRes.status}).`
            );
          }
        }

        if (!activeAttempt && !(Number.isFinite(numericAttemptIdOverride) && numericAttemptIdOverride > 0)) {
          const attemptsRes = await requestFn(PATIENT_ASSESSMENT_ATTEMPTS_API);
          const attemptsPayload = await attemptsRes.json();
          const allAttempts = normalizeApiRows(attemptsPayload);

          const matchingAttempts = allAttempts.filter((attempt) => {
            const attemptStatus = String(attempt?.status ?? "").toLowerCase();
            if (!["assigned", "in_progress"].includes(attemptStatus)) return false;

            return (
              getAttemptPatientIdValue(attempt) === numericPatientId &&
              getAttemptAssessmentIdValue(attempt) === numericAssessmentId
            );
          });

          activeAttempt =
            matchingAttempts.find(
              (attempt) => String(attempt?.status ?? "").toLowerCase() === "in_progress"
            ) || matchingAttempts[0] || null;
        }

        if (!activeAttempt && !(Number.isFinite(numericAttemptIdOverride) && numericAttemptIdOverride > 0)) {
          const defaultItem = initializationQuestions[0] ?? null;
          const createAttemptRes = await requestFn(PATIENT_ASSESSMENT_ATTEMPTS_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              patient_id: numericPatientId,
              assessment_id: numericAssessmentId,
              status: "in_progress",
              current_question_id: getQuestionIdForItem(defaultItem) || null,
              current_section_id: getSectionIdForItem(defaultItem) || null,
              ...(Number.isFinite(numericPatientEventId) && numericPatientEventId > 0
                ? { patient_event_id: numericPatientEventId }
                : {}),
            }),
          });

          activeAttempt = await createAttemptRes.json();
        }
        mark("attemptResolvedAt");

        const resolvedAttemptId = getAttemptIdValue(activeAttempt);
        if (!resolvedAttemptId) {
          throw new Error("Unable to resolve patient assessment attempt id.");
        }

        const startIndex = Math.max(0, resolveStartIndex(activeAttempt));
        const startItem = initializationQuestions[startIndex] ?? initializationQuestions[0] ?? null;

        if (String(activeAttempt?.status ?? "").toLowerCase() !== "in_progress") {
          await requestFn(`${PATIENT_ASSESSMENT_ATTEMPTS_API}${resolvedAttemptId}/`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              status: "in_progress",
              current_question_id: getQuestionIdForItem(startItem) || null,
              current_section_id: getSectionIdForItem(startItem) || null,
              ...(Number.isFinite(numericPatientEventId) && numericPatientEventId > 0
                ? { patient_event_id: numericPatientEventId }
                : {}),
            }),
          });
        }

        if (!cancelled) {
          setAttemptId(resolvedAttemptId);
          setCurrentIndex(startIndex);
          setResponsesWithRef({});
          setSubquestionSelectionsWithRef({});
          responseIdByQuestionIdRef.current = {};
          setIsAttemptReady(true);
        }

        logInitPerf("ready-shell", {
          attemptId: resolvedAttemptId,
          responseCount: 0,
        });

        mark("responseLoadStartedAt");
        const attemptResponses = await loadResponsesForAttempt(resolvedAttemptId);
        mark("responsesLoadedAt");

        const {
          loadedResponses,
          loadedResponseIdByQuestionId,
        } = mapAttemptResponsesToState(attemptResponses);
        const loadedSubquestionSelections = buildSubquestionSelectionsByQuestionId(loadedResponses);

        if (!cancelled) {
          // Keep any user-entered values that occurred before background load completed.
          setResponsesWithRef((prev) => ({
            ...loadedResponses,
            ...prev,
          }));
          setSubquestionSelectionsWithRef((prev) => ({
            ...loadedSubquestionSelections,
            ...prev,
          }));

          responseIdByQuestionIdRef.current = {
            ...loadedResponseIdByQuestionId,
            ...responseIdByQuestionIdRef.current,
          };
        }

        logInitPerf("ready-responses", {
          attemptId: resolvedAttemptId,
          responseCount: attemptResponses.length,
        });
      } catch (error) {
        console.error("Failed to initialize patient assessment attempt", error);
        if (!cancelled) {
          setAttemptId(null);
          responseIdByQuestionIdRef.current = {};
          setCurrentIndex(0);
          setResponsesWithRef({});
          setSubquestionSelectionsWithRef({});
          setIsAttemptReady(true);
        }
        logInitPerf("failed");
      }
    };

    initializeRunState();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    questionsInitializationKey,
    startQuestionSectionId,
    canPersistAttempts,
    numericPatientId,
    numericAssessmentId,
    numericPatientEventId,
    numericAttemptIdOverride,
    requestFn,
  ]);

  const highestPriorityRuleBySourceQuestionId = useMemo(() => {
    const bySourceQuestionId = new Map();

    (flowRules || []).forEach((rule) => {
      const sourceQuestionId = getSourceQuestionIdFromRule(rule);
      if (!sourceQuestionId) return;

      const existing = bySourceQuestionId.get(sourceQuestionId);
      if (!existing || getRulePriorityValue(rule) > getRulePriorityValue(existing)) {
        bySourceQuestionId.set(sourceQuestionId, rule);
      }
    });

    return bySourceQuestionId;
  }, [flowRules]);

  const runtimeQuestions = useMemo(() => {
    const expanded = [];
    const forcedSourceQuestionId = Number(forceConditionalSourceQuestionId);
    const forcedTargetSectionId = Number(forceConditionalTargetSectionId);
    const hasForcedSourceQuestionId =
      Number.isFinite(forcedSourceQuestionId) && forcedSourceQuestionId > 0;
    const hasForcedTargetSectionId =
      Number.isFinite(forcedTargetSectionId) && forcedTargetSectionId > 0;

    questions.forEach((item) => {
      expanded.push(item);

      const sourceQuestionId = getQuestionIdForItem(item);
      if (!sourceQuestionId) return;

      const matchingRule = highestPriorityRuleBySourceQuestionId.get(sourceQuestionId);
      const ruleTargetSectionId = getTargetSectionIdFromRule(matchingRule);
      const currentResponseValue = responses[sourceQuestionId];
      const shouldEnterConditionalFlow =
        !!matchingRule &&
        doesRuleMatchResponseValue(matchingRule, currentResponseValue);
      const shouldForceConditionalFlow =
        hasForcedSourceQuestionId &&
        hasForcedTargetSectionId &&
        sourceQuestionId === forcedSourceQuestionId;

      if (!shouldEnterConditionalFlow && !shouldForceConditionalFlow) return;

      const targetSectionId = shouldForceConditionalFlow
        ? forcedTargetSectionId
        : ruleTargetSectionId;
      if (!targetSectionId) return;

      const conditionalQuestions = conditionalQuestionSectionsBySectionId[targetSectionId] || [];
      if (!conditionalQuestions.length) return;

      conditionalQuestions.forEach((questionSection) => {
        const questionSectionId = Number(
          questionSection?.question_section_id ??
          questionSection?.question_section?.question_section_id ??
          0
        );
        if (skippedQuestionSectionIdSet.has(questionSectionId)) return;

        expanded.push({
          assessmentSectionId: null,
          sectionId: targetSectionId,
          sectionTitle:
            matchingRule?.target_section?.title ??
            questionSection?.section?.title ??
            item.sectionTitle,
          sectionOrder: Number(item.sectionOrder ?? 0),
          questionOrder: getQuestionOrder(questionSection),
          isRequired: toBooleanRequired(
            questionSection?.is_required ??
              questionSection?.question_section?.is_required ??
              false
          ),
          questionSectionId,
          questionSection,
          question: questionSection?.question ?? {},
          isConditionalFlowQuestion: true,
          conditionalFlowSourceQuestionId: sourceQuestionId,
          conditionalFlowTargetSectionId: targetSectionId,
        });
      });
    });

    if (hasForcedTargetSectionId) {
      const hasForcedTargetInExpanded = expanded.some((item) => (
        item?.isConditionalFlowQuestion &&
        Number(item?.conditionalFlowTargetSectionId) === forcedTargetSectionId
      ));

      if (!hasForcedTargetInExpanded) {
        const fallbackConditionalQuestions =
          conditionalQuestionSectionsBySectionId[forcedTargetSectionId] || [];

        fallbackConditionalQuestions.forEach((questionSection) => {
          const questionSectionId = Number(
            questionSection?.question_section_id ??
            questionSection?.question_section?.question_section_id ??
            0
          );
          if (skippedQuestionSectionIdSet.has(questionSectionId)) return;

          expanded.push({
            assessmentSectionId: null,
            sectionId: forcedTargetSectionId,
            sectionTitle:
              questionSection?.section?.title ??
              "Conditional Flow",
            sectionOrder: Number.MAX_SAFE_INTEGER,
            questionOrder: getQuestionOrder(questionSection),
            isRequired: toBooleanRequired(
              questionSection?.is_required ??
                questionSection?.question_section?.is_required ??
                false
            ),
            questionSectionId,
            questionSection,
            question: questionSection?.question ?? {},
            isConditionalFlowQuestion: true,
            conditionalFlowSourceQuestionId: hasForcedSourceQuestionId
              ? forcedSourceQuestionId
              : null,
            conditionalFlowTargetSectionId: forcedTargetSectionId,
          });
        });
      }
    }

    return expanded;
  }, [
    questions,
    responses,
    highestPriorityRuleBySourceQuestionId,
    conditionalQuestionSectionsBySectionId,
    forceConditionalSourceQuestionId,
    forceConditionalTargetSectionId,
    skippedQuestionSectionIdSet,
  ]);

  useEffect(() => {
    setCurrentIndex((prev) => {
      if (!runtimeQuestions.length) return 0;
      if (prev < 0) return 0;
      if (prev >= runtimeQuestions.length) return runtimeQuestions.length - 1;
      return prev;
    });
  }, [runtimeQuestions]);

  useEffect(() => {
    if (!isOpen) {
      appliedStartRequestRef.current = null;
      return;
    }

    if (!isAttemptReady || !runtimeQuestions.length) return;

    const requestedSectionId = Number(startQuestionSectionId);
    const requestedQuestionId = Number(startQuestionId);
    const forcedSourceQuestionId = Number(forceConditionalSourceQuestionId);
    const forcedTargetSectionId = Number(forceConditionalTargetSectionId);
    const hasRequestedSectionId = Number.isFinite(requestedSectionId) && requestedSectionId > 0;
    const hasRequestedQuestionId = Number.isFinite(requestedQuestionId) && requestedQuestionId > 0;
    const hasForcedSourceQuestionId =
      Number.isFinite(forcedSourceQuestionId) && forcedSourceQuestionId > 0;
    const hasForcedTargetSectionId =
      Number.isFinite(forcedTargetSectionId) && forcedTargetSectionId > 0;

    if (!hasRequestedSectionId && !hasRequestedQuestionId && !hasForcedSourceQuestionId) {
      setIsResolvingRequestedStart(false);
      return;
    }

    const requestKey = `${hasRequestedSectionId ? requestedSectionId : "-"}:${hasRequestedQuestionId ? requestedQuestionId : "-"}:${hasForcedSourceQuestionId ? forcedSourceQuestionId : "-"}:${hasForcedTargetSectionId ? forcedTargetSectionId : "-"}`;
    if (appliedStartRequestRef.current === requestKey) return;

    let targetIndex = -1;

    if (hasRequestedSectionId) {
      targetIndex = runtimeQuestions.findIndex(
        (item) => Number(item?.questionSectionId) === requestedSectionId
      );
    }

    if (targetIndex < 0 && hasRequestedQuestionId) {
      targetIndex = runtimeQuestions.findIndex(
        (item) => getQuestionIdForItem(item) === requestedQuestionId
      );
    }

    if (targetIndex < 0 && hasForcedSourceQuestionId) {
      targetIndex = runtimeQuestions.findIndex((item) => {
        if (!item?.isConditionalFlowQuestion) return false;

        const itemSourceQuestionId = Number(item?.conditionalFlowSourceQuestionId);
        const itemTargetSectionId = Number(item?.conditionalFlowTargetSectionId);

        if (itemSourceQuestionId !== forcedSourceQuestionId) return false;
        if (hasForcedTargetSectionId && itemTargetSectionId !== forcedTargetSectionId) return false;

        return true;
      });
    }

    if (targetIndex >= 0) {
      setCurrentIndex(targetIndex);
      appliedStartRequestRef.current = requestKey;
      setIsResolvingRequestedStart(false);
      return;
    }

    if (!isConditionalFlowDataLoading) {
      appliedStartRequestRef.current = requestKey;
      setIsResolvingRequestedStart(false);
    }
  }, [
    isOpen,
    isAttemptReady,
    isConditionalFlowDataLoading,
    runtimeQuestions,
    startQuestionSectionId,
    startQuestionId,
    forceConditionalSourceQuestionId,
    forceConditionalTargetSectionId,
  ]);

  const totalQuestions = runtimeQuestions.length;
  const currentItem = runtimeQuestions[currentIndex] ?? null;
  const progressPercent =
    totalQuestions > 0 ? Math.round(((currentIndex + 1) / totalQuestions) * 100) : 0;
  const question = currentItem?.question ?? {};
  const questionId =
    question?.question_id ??
    currentItem?.questionSection?.question_id ??
    `${currentItem?.sectionId ?? "section"}-${currentItem?.questionOrder ?? currentIndex + 1}`;

  const questionType = normalizeQuestionType(question);
  const isInjuryEventDateQuestion =
    isInjuryEventDatePrompt(question?.question) || isInjuryEventDatePrompt(question?.title);
  const isZipcodeQuestion = questionType === "zipcode" || questionType === "zip_code";
  const isEmailResponseQuestion = questionType === "email_response";
  const isSignatureAgreementQuestion = questionType === "signature_agreement";
  const isNoResponseQuestion =
    questionType === "no_response" || questionType === "perform_task_video";
  const currentResponse =
    responses[questionId] ?? getInitialResponse(questionType);
  const isCurrentQuestionRequired = !!currentItem?.isRequired;
  const hasCurrentResponse = hasResponseValue(currentResponse);
  const isSignatureDrawQuestion = questionType === "signature_draw";
  const signatureResponse =
    currentResponse && typeof currentResponse === "object" && !Array.isArray(currentResponse)
      ? currentResponse
      : {};
  const hasSignatureDrawing = String(signatureResponse?.signature_data_url ?? "").trim().length > 0;
  const hasSignatureName = String(signatureResponse?.full_name ?? "").trim().length > 0;
  const signatureAgreementResponse =
    currentResponse && typeof currentResponse === "object" && !Array.isArray(currentResponse)
      ? currentResponse
      : {};
  const currentQuestionHasSubquestion = getQuestionSectionBoolean(
    currentItem?.questionSection,
    "has_subquestion"
  );
  const currentSubQuestionPrompt = getLocalizedQuestionSectionString(
    currentItem?.questionSection,
    "sub_question_prompt",
    normalizedPreferredLanguageCode
  );
  const currentSubQuestionType = getQuestionSectionValue(
    currentItem?.questionSection,
    "sub_question_type"
  );
  const currentSubQuestionOptions = normalizeOptionItems(
    currentSubQuestionType?.options,
    normalizedPreferredLanguageCode
  );
  const currentSubQuestionTypeLabel = getQuestionSectionTypeLabel(
    currentItem?.questionSection,
    "sub_question_type"
  );
  const hasAgreementDocumentViewed = Boolean(signatureAgreementResponse?.document_viewed);
  const hasAgreementSignatureDrawing =
    String(signatureAgreementResponse?.signature_data_url ?? "").trim().length > 0;
  const hasAgreementSignatureName =
    String(signatureAgreementResponse?.full_name ?? "").trim().length > 0;
  const isIncompleteSignatureAgreement =
    isSignatureAgreementQuestion &&
    (!hasAgreementDocumentViewed || !hasAgreementSignatureDrawing || !hasAgreementSignatureName);

  const shouldAutoSkipCurrentQuestion = isInjuryEventDateQuestion && hasCurrentResponse;

  useEffect(() => {
    setActiveSubquestionPopup(null);
    setActiveOptionMoreInfoCard(null);
    clearOptionMoreInfoTimer();
  }, [questionId]);

  const getOptionMoreInfoText = (optionItem) => String(optionItem?.option_more_info ?? "").trim();

  const getOptionMoreInfoIdentity = (optionItem) => buildSelectableItemIdentity(optionItem);

  const getOptionMoreInfoAnchorPosition = (anchorRect) => {
    const fallbackWidth = 420;
    const cardWidth = Math.min(fallbackWidth, Math.max(240, window.innerWidth - 32));
    const gap = 10;
    const viewportPadding = 12;
    const rawLeft = (anchorRect?.right ?? 0) - cardWidth;
    const rawTop = (anchorRect?.bottom ?? 0) + gap;
    const maxLeft = Math.max(viewportPadding, window.innerWidth - cardWidth - viewportPadding);
    const left = Math.min(Math.max(viewportPadding, rawLeft), maxLeft);
    const maxTop = Math.max(viewportPadding, window.innerHeight - 220);
    const top = Math.min(Math.max(viewportPadding, rawTop), maxTop);

    return {
      left,
      top,
      width: cardWidth,
    };
  };

  const openOptionMoreInfoCard = (optionItem, trigger = "hover", anchorRect = null) => {
    const optionIdentity = getOptionMoreInfoIdentity(optionItem);
    const infoText = getOptionMoreInfoText(optionItem);

    if (!optionIdentity || !infoText) return;

    const anchorPosition = getOptionMoreInfoAnchorPosition(anchorRect);

    setActiveOptionMoreInfoCard({
      questionId,
      optionIdentity,
      optionLabel: String(optionItem?.option ?? "").trim(),
      infoText,
      trigger,
      anchorPosition,
    });
  };

  const scheduleOptionMoreInfoCard = (optionItem, anchorRect = null) => {
    const optionIdentity = getOptionMoreInfoIdentity(optionItem);
    const infoText = getOptionMoreInfoText(optionItem);

    if (!optionIdentity || !infoText) return;

    clearOptionMoreInfoTimer();
    optionMoreInfoTimerRef.current = window.setTimeout(() => {
      setActiveOptionMoreInfoCard((prev) => {
        if (
          prev?.questionId === questionId &&
          prev?.optionIdentity === optionIdentity &&
          prev?.trigger === "click"
        ) {
          return prev;
        }

        return {
          questionId,
          optionIdentity,
          optionLabel: String(optionItem?.option ?? "").trim(),
          infoText,
          trigger: "hover",
          anchorPosition: getOptionMoreInfoAnchorPosition(anchorRect),
        };
      });
      optionMoreInfoTimerRef.current = null;
    }, OPTION_MORE_INFO_TOOLTIP_DELAY_MS);
  };

  const hideHoveredOptionMoreInfoCard = (optionItem) => {
    const optionIdentity = getOptionMoreInfoIdentity(optionItem);
    clearOptionMoreInfoTimer();

    if (!optionIdentity) return;

    setActiveOptionMoreInfoCard((prev) => {
      if (
        prev?.questionId === questionId &&
        prev?.optionIdentity === optionIdentity &&
        prev?.trigger === "hover"
      ) {
        return null;
      }

      return prev;
    });
  };

  const renderSelectableOptionRow = ({
    optionItem,
    inputType,
    inputName,
    checked,
    value,
    onChange,
    trailingContent = null,
  }) => {
    const optionIdentity = getOptionMoreInfoIdentity(optionItem);
    const optionMoreInfoText = getOptionMoreInfoText(optionItem);
    const hasOptionMoreInfo = Boolean(optionIdentity) && Boolean(optionMoreInfoText);

    return (
      <label
        className={`run-assessment-radio-row ${hasOptionMoreInfo ? "run-assessment-radio-row-has-info" : ""}`}
        key={`${optionItem.order}-${optionItem.option}`}
        onMouseEnter={(event) => {
          if (hasOptionMoreInfo) {
            const buttonRect = event.currentTarget
              .querySelector(".run-assessment-option-more-info-button")
              ?.getBoundingClientRect?.();
            scheduleOptionMoreInfoCard(optionItem, buttonRect ?? event.currentTarget.getBoundingClientRect());
          }
        }}
        onMouseLeave={() => {
          if (hasOptionMoreInfo) {
            hideHoveredOptionMoreInfoCard(optionItem);
          }
        }}
      >
        {hasOptionMoreInfo && (
          <button
            type="button"
            className="run-assessment-option-more-info-button"
            aria-label={`More info for ${optionItem.option}`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              clearOptionMoreInfoTimer();
              const anchorRect = event.currentTarget.getBoundingClientRect();
              const isSameClickCardOpen =
                activeOptionMoreInfoCard?.questionId === questionId &&
                activeOptionMoreInfoCard?.optionIdentity === optionIdentity &&
                activeOptionMoreInfoCard?.trigger === "click";

              if (isSameClickCardOpen) {
                setActiveOptionMoreInfoCard(null);
                return;
              }

              openOptionMoreInfoCard(optionItem, "click", anchorRect);
            }}
          >
            <span aria-hidden="true">i</span>
          </button>
        )}

        <span className="run-assessment-radio-row-main">
          <input
            type={inputType}
            name={inputName}
            value={value}
            checked={checked}
            onChange={onChange}
          />
          <span className="run-assessment-option-label-wrap">
            <span>{optionItem.option}</span>
            {trailingContent}
          </span>
        </span>
      </label>
    );
  };

  const currentQuestionSubquestionSelections =
    subquestionSelectionsByQuestionId[questionId] ??
    getSubquestionSelectionsFromResponseValue(currentResponse);
  const filteredCurrentQuestionSubquestionSelections = Object.entries(
    currentQuestionSubquestionSelections ?? {}
  ).reduce((accumulator, [parentOptionId, selection]) => {
    const matchingSelectedItem = Array.isArray(currentResponse)
      ? currentResponse.find((item) => buildSelectableItemIdentity(item) === parentOptionId)
      : null;

    if (!matchingSelectedItem || !shouldAllowSubquestionForAnswerValue(matchingSelectedItem?.value)) {
      return accumulator;
    }

    return {
      ...accumulator,
      [parentOptionId]: selection,
    };
  }, {});

  const activeSubquestionSelection =
    activeSubquestionPopup && activeSubquestionPopup.questionId === questionId
      ? filteredCurrentQuestionSubquestionSelections[activeSubquestionPopup.parentOptionId] ?? null
      : null;

  const persistResponseAndAttemptState = async ({
    targetIndex,
    status,
    markComplete = false,
  }) => {
    if (!canPersistAttempts || !attemptId) return;

    const questionNumericId = Number(questionId);
    const hasValidQuestionId = Number.isFinite(questionNumericId) && questionNumericId > 0;
    const currentResponseSnapshot = responsesRef.current[questionId] ?? currentResponse;
    const currentSubquestionSelectionsSnapshot =
      subquestionSelectionsByQuestionIdRef.current[questionId] ??
      getSubquestionSelectionsFromResponseValue(currentResponseSnapshot);
    const filteredSubquestionSelectionsSnapshot = Object.entries(
      currentSubquestionSelectionsSnapshot ?? {}
    ).reduce((accumulator, [parentOptionId, selection]) => {
      const matchingSelectedItem = Array.isArray(currentResponseSnapshot)
        ? currentResponseSnapshot.find((item) => buildSelectableItemIdentity(item) === parentOptionId)
        : null;

      if (!matchingSelectedItem || !shouldAllowSubquestionForAnswerValue(matchingSelectedItem?.value)) {
        return accumulator;
      }

      return {
        ...accumulator,
        [parentOptionId]: selection,
      };
    }, {});
    const answerPayload = getPersistedAnswerPayload(question, currentResponseSnapshot, {
      hasSubquestion: currentQuestionHasSubquestion,
      subquestionSelections: filteredSubquestionSelectionsSnapshot,
      languageCode: normalizedPreferredLanguageCode,
    });
    const shouldIncludeScoreValue = toBooleanRequired(
      currentItem?.questionSection?.include_sum_total ??
      currentItem?.questionSection?.question_section?.include_sum_total ??
      false
    );
    const calculatedQuestionValue = getScoreValueFromAnswer(answerPayload);
    const scoreValuePayload = toApiDecimalString(
      shouldIncludeScoreValue ? calculatedQuestionValue : 0,
      2
    );
    const calcValuePayload = toApiDecimalString(calculatedQuestionValue ?? 0, 2);

    if (hasValidQuestionId) {
      const existingResponseId = responseIdByQuestionIdRef.current[questionNumericId] ?? null;

      const createPatientResponse = async (payloadCandidates) => {
        let lastStatus = null;
        let lastErrorText = "";
        const failureDetails = [];

        for (let index = 0; index < payloadCandidates.length; index += 1) {
          const payload = payloadCandidates[index];
          const createRes = await requestFn(PATIENT_RESPONSES_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });

          if (createRes.ok) {
            return createRes;
          }

          lastStatus = createRes.status;
          try {
            lastErrorText = await createRes.text();
          } catch {
            lastErrorText = "";
          }

          failureDetails.push({
            status: createRes.status,
            payload,
            error: lastErrorText,
          });

          const shouldTryAlternateAttemptField =
            index < payloadCandidates.length - 1 &&
            createRes.status === 400 &&
            shouldRetryPatientResponseCreateForAttemptField(lastErrorText);

          if (!shouldTryAlternateAttemptField) {
            break;
          }
        }

        throw new Error(
          `Create response failed (${lastStatus ?? "unknown"}) ${lastErrorText} :: ${JSON.stringify(failureDetails)}`
        );
      };

      if (existingResponseId) {
        let patchRes = await requestFn(`${PATIENT_RESPONSES_API}${existingResponseId}/`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            answer_value: answerPayload,
            score_value: scoreValuePayload,
            calc_value: calcValuePayload,
          }),
        });

        if (!patchRes.ok) {
          patchRes = await requestFn(`${PATIENT_RESPONSES_API}${existingResponseId}/`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              answer_value: answerPayload,
              score_value: scoreValuePayload,
              calc_value: calcValuePayload,
            }),
          });

          if (!patchRes.ok) {
            const patchErrorBody = await patchRes.text().catch(() => "");
            throw new Error(
              `Update response failed (${patchRes.status}) ${patchErrorBody}`
            );
          }
        }
      } else {
        const requestContext = {
          patientId: Number.isFinite(numericPatientId) && numericPatientId > 0 ? numericPatientId : null,
          assessmentId: Number.isFinite(numericAssessmentId) && numericAssessmentId > 0 ? numericAssessmentId : null,
          sectionId: Number.isFinite(Number(currentItem?.assessmentSectionId))
            ? Number(currentItem?.assessmentSectionId)
            : Number.isFinite(Number(currentItem?.sectionId))
              ? Number(currentItem?.sectionId)
              : null,
          questionTypeId: Number.isFinite(Number(question?.question_type?.question_type_id))
            ? Number(question?.question_type?.question_type_id)
            : Number.isFinite(Number(question?.question_type_id))
              ? Number(question?.question_type_id)
              : null,
        };

        const baseCreatePayload = {
          question_id: questionNumericId,
          answer_value: answerPayload,
          score_value: scoreValuePayload,
          calc_value: calcValuePayload,
        };

        const withOptionalContext = (payload) => ({
          ...payload,
          ...(requestContext.patientId ? { patient_id: requestContext.patientId } : {}),
          ...(requestContext.assessmentId ? { assessment_id: requestContext.assessmentId } : {}),
          ...(requestContext.sectionId ? { section_id: requestContext.sectionId } : {}),
          ...(requestContext.questionTypeId ? { question_type_id: requestContext.questionTypeId } : {}),
        });

        const createPayloadCandidates = [
          withOptionalContext({
            attempt_id: attemptId,
            ...baseCreatePayload,
          }),
          withOptionalContext({
            patient_assessment_attempt_id: attemptId,
            ...baseCreatePayload,
          }),
          withOptionalContext({
            assessment_attempt_id: attemptId,
            ...baseCreatePayload,
          }),
          withOptionalContext({
            patient_assessment_attempt: attemptId,
            ...baseCreatePayload,
          }),
          {
            attempt_id: attemptId,
            question_id: questionNumericId,
            answer_value: answerPayload,
            score_value: scoreValuePayload,
            calc_value: calcValuePayload,
          },
          {
            patient_assessment_attempt_id: attemptId,
            question_id: questionNumericId,
            answer_value: answerPayload,
            score_value: scoreValuePayload,
            calc_value: calcValuePayload,
          },
          {
            assessment_attempt_id: attemptId,
            question_id: questionNumericId,
            answer_value: answerPayload,
            score_value: scoreValuePayload,
            calc_value: calcValuePayload,
          },
          {
            patient_assessment_attempt: attemptId,
            question_id: questionNumericId,
            answer_value: answerPayload,
            score_value: scoreValuePayload,
            calc_value: calcValuePayload,
          },
        ];

        const createResponseRes = await createPatientResponse(createPayloadCandidates);

        const createdResponse = await createResponseRes.json();
        const createdResponseId = getResponseRecordId(createdResponse);
        if (createdResponseId) {
          responseIdByQuestionIdRef.current = {
            ...responseIdByQuestionIdRef.current,
            [questionNumericId]: createdResponseId,
          };
        }
      }
    }

    const targetItem = runtimeQuestions[targetIndex] ?? null;
    const nextQuestionId = getQuestionIdForItem(targetItem) || null;
    const nextSectionId = getSectionIdForItem(targetItem) || null;

    let attemptRes = await requestFn(`${PATIENT_ASSESSMENT_ATTEMPTS_API}${attemptId}/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        status,
        current_question_id: nextQuestionId,
        current_section_id: nextSectionId,
        ...(markComplete ? { completed_at: new Date().toISOString() } : {}),
      }),
    });

    if (!attemptRes.ok) {
      await requestFn(`${PATIENT_ASSESSMENT_ATTEMPTS_API}${attemptId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status,
          current_question_id: nextQuestionId,
          current_section_id: nextSectionId,
          ...(markComplete ? { completed_at: new Date().toISOString() } : {}),
        }),
      });
    }
  };

  const fetchAttemptTotalScore = async (attemptNumericId) => {
    const resolvedAttemptId = Number(attemptNumericId);
    if (!Number.isFinite(resolvedAttemptId) || resolvedAttemptId <= 0) {
      throw new Error("Missing attempt id for total score lookup");
    }

    const fetchJson = async (path) => {
      const res = await requestFn(path);
      if (!res.ok) {
        const message = await res.text().catch(() => "");
        throw new Error(`Total score request failed (${res.status}) ${message}`);
      }
      return res.json();
    };

    try {
      return await fetchJson(`${API_BASE}/api/total-score/${resolvedAttemptId}/`);
    } catch {
      return fetchJson(`${API_BASE}/api/total-score/${resolvedAttemptId}`);
    }
  };

  const persistAssessment1001AttemptMetrics = async (attemptNumericId) => {
    if (numericAssessmentId !== 1001) return;

    const resolvedAttemptId = Number(attemptNumericId);
    if (!Number.isFinite(resolvedAttemptId) || resolvedAttemptId <= 0) {
      throw new Error("Missing attempt id for assessment 1001 metric calculation");
    }

    const historyRes = await requestFn(
      `${PATIENT_RESPONSES_HISTORY_API}?assessment_attempt_id=${resolvedAttemptId}`
    );

    if (!historyRes.ok) {
      const message = await historyRes.text().catch(() => "");
      throw new Error(
        `Assessment 1001 metric history request failed (${historyRes.status}) ${message}`
      );
    }

    const historyPayload = await historyRes.json();
    const historyRows = normalizeApiRows(historyPayload);
    const matchingRows = historyRows.filter((responseItem) => {
      const responseAttemptId = getAssessmentResponseAttemptId(responseItem);
      return !responseAttemptId || responseAttemptId === resolvedAttemptId;
    });
    const metrics = computeAssessment1001AttemptMetrics(matchingRows);
    const classificationsRes = await requestFn(
      `${ASSESSMENT_CLASSIFICATIONS_API}?assessment_id=${numericAssessmentId}`
    );

    if (!classificationsRes.ok) {
      const message = await classificationsRes.text().catch(() => "");
      throw new Error(
        `Assessment classification request failed (${classificationsRes.status}) ${message}`
      );
    }

    const classificationsPayload = await classificationsRes.json();
    const classificationRows = normalizeApiRows(classificationsPayload);
    const classification = resolveAttemptClassification(
      classificationRows,
      metrics.class_calc_value
    );

    const patchRes = await requestFn(`${PATIENT_ASSESSMENT_ATTEMPTS_API}${resolvedAttemptId}/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        status: "completed",
        ...metrics,
        classification,
      }),
    });

    if (!patchRes.ok) {
      const message = await patchRes.text().catch(() => "");
      throw new Error(
        `Assessment 1001 metric patch failed (${patchRes.status}) ${message}`
      );
    }
  };

  const clearPersistedResponsesByQuestionIds = async (questionIds) => {
    if (!canPersistAttempts || !questionIds?.length) return;

    const unresolvedQuestionIds = new Set();

    for (const id of questionIds) {
      const numericQuestionId = Number(id);
      if (!Number.isFinite(numericQuestionId) || numericQuestionId <= 0) continue;

      const responseRecordId = responseIdByQuestionIdRef.current[numericQuestionId];
      if (!responseRecordId) {
        unresolvedQuestionIds.add(numericQuestionId);
        continue;
      }

      try {
        await requestFn(`${PATIENT_RESPONSES_API}${responseRecordId}/`, {
          method: "DELETE",
        });
      } catch (error) {
        console.error("Failed to delete stale conditional response", error);
      }

      delete responseIdByQuestionIdRef.current[numericQuestionId];
    }

    if (!unresolvedQuestionIds.size || !attemptId) return;

    const attemptNumericId = Number(attemptId);
    if (!Number.isFinite(attemptNumericId) || attemptNumericId <= 0) return;

    const responseQueryPaths = [
      `${PATIENT_RESPONSES_HISTORY_API}?assessment_attempt_id=${attemptNumericId}`,
      `${PATIENT_RESPONSES_API}?attempt_id=${attemptNumericId}`,
      `${PATIENT_RESPONSES_API}?assessment_attempt_id=${attemptNumericId}`,
      `${PATIENT_RESPONSES_API}?attempt=${attemptNumericId}`,
      `${PATIENT_RESPONSES_API}?patient_assessment_attempt_id=${attemptNumericId}`,
    ];

    let matchingResponseRows = [];

    for (const responsePath of responseQueryPaths) {
      try {
        const scopedRes = await requestFn(responsePath);
        if (!scopedRes.ok) continue;

        const scopedPayload = await scopedRes.json();
        const scopedRows = normalizeApiRows(scopedPayload);

        matchingResponseRows = scopedRows.filter((responseItem) => {
          const responseQuestionId = getResponseQuestionId(responseItem);
          if (!responseQuestionId || !unresolvedQuestionIds.has(responseQuestionId)) return false;

          const responseAttemptId = getResponseAttemptId(responseItem);
          return !responseAttemptId || responseAttemptId === attemptNumericId;
        });

        if (matchingResponseRows.length) {
          break;
        }
      } catch {
        // Try next endpoint variant.
      }
    }

    for (const responseItem of matchingResponseRows) {
      const responseRecordId = getResponseRecordId(responseItem);
      const responseQuestionId = getResponseQuestionId(responseItem);

      if (!responseRecordId || !responseQuestionId) continue;

      try {
        await requestFn(`${PATIENT_RESPONSES_API}${responseRecordId}/`, {
          method: "DELETE",
        });
      } catch (error) {
        console.error("Failed to delete stale conditional response", error);
      }

      delete responseIdByQuestionIdRef.current[responseQuestionId];
    }
  };

  const clearPersistedResponsesByTargetSectionId = async (targetSectionId) => {
    if (!canPersistAttempts || !attemptId) return;

    const numericTargetSectionId = Number(targetSectionId);
    if (!Number.isFinite(numericTargetSectionId) || numericTargetSectionId <= 0) return;

    const targetQuestionSections = conditionalQuestionSectionsBySectionId[numericTargetSectionId] || [];
    const targetQuestionIds = new Set(
      targetQuestionSections
        .map((questionSection) => Number(questionSection?.question?.question_id ?? 0))
        .filter((id) => Number.isFinite(id) && id > 0)
    );

    if (!targetQuestionIds.size) return;

    const attemptNumericId = Number(attemptId);
    if (!Number.isFinite(attemptNumericId) || attemptNumericId <= 0) return;

    const questionIdList = Array.from(targetQuestionIds);
    const questionParam = encodeURIComponent(`[${questionIdList.join(",")}]`);
    const byFilterPath = `${API_BASE}/api/patient-responses/by-filter/?attempt=${attemptNumericId}&question=${questionParam}`;

    try {
      const byFilterRes = await requestFn(byFilterPath, {
        method: "DELETE",
      });

      if (!byFilterRes.ok) {
        const byFilterError = await byFilterRes.text().catch(() => "");
        console.error(
          `Failed to delete conditional target-section responses by filter (${byFilterRes.status}) ${byFilterError}`
        );
      }
    } catch (error) {
      console.error("Failed to delete conditional target-section responses by filter", error);
    }

    questionIdList.forEach((id) => {
      delete responseIdByQuestionIdRef.current[id];
    });
  };

  const setResponse = (value) => {
    if (signatureUploadError) {
      setSignatureUploadError("");
    }

    const sourceQuestionId = Number(questionId);
    const sourceRule = highestPriorityRuleBySourceQuestionId.get(sourceQuestionId);
    let questionIdsToClear = [];
    let targetSectionIdToClear = null;

    setResponsesWithRef((prev) => {
      const resolvedValue = typeof value === "function" ? value(prev[questionId]) : value;
      const sanitizedResolvedValue =
        currentQuestionHasSubquestion && Array.isArray(resolvedValue)
          ? resolvedValue.map((item) => {
              if (shouldAllowSubquestionForAnswerValue(item?.value)) {
                return item;
              }

              if (!Object.prototype.hasOwnProperty.call(item ?? {}, "sub_answer")) {
                return item;
              }

              const nextItem = {
                ...item,
              };
              delete nextItem.sub_answer;
              return nextItem;
            })
          : resolvedValue;
      const next = {
        ...prev,
        [questionId]: sanitizedResolvedValue,
      };

      if (currentQuestionHasSubquestion && Array.isArray(sanitizedResolvedValue)) {
        const allowedParentOptionIds = new Set(
          sanitizedResolvedValue
            .filter((item) => shouldAllowSubquestionForAnswerValue(item?.value))
            .map((item) => buildSelectableItemIdentity(item))
            .filter(Boolean)
        );

        setSubquestionSelectionsWithRef((previousSelections) => {
          const currentSelections = previousSelections[questionId] ?? {};
          const nextSelections = Object.entries(currentSelections).reduce(
            (accumulator, [parentOptionId, selection]) => {
              if (!allowedParentOptionIds.has(parentOptionId)) {
                return accumulator;
              }

              return {
                ...accumulator,
                [parentOptionId]: selection,
              };
            },
            {}
          );

          if (Object.keys(nextSelections).length === Object.keys(currentSelections).length) {
            return previousSelections;
          }

          const nextAllSelections = { ...previousSelections };
          if (Object.keys(nextSelections).length) {
            nextAllSelections[questionId] = nextSelections;
          } else {
            delete nextAllSelections[questionId];
          }
          return nextAllSelections;
        });

        if (
          activeSubquestionPopup?.questionId === questionId &&
          !allowedParentOptionIds.has(activeSubquestionPopup.parentOptionId)
        ) {
          setActiveSubquestionPopup(null);
        }
      }

      if (!sourceRule) return next;

      const matchedAfter = doesRuleMatchResponseValue(sourceRule, sanitizedResolvedValue);

      if (!matchedAfter) {
        const targetSectionId = getTargetSectionIdFromRule(sourceRule);
        targetSectionIdToClear = targetSectionId;
        const staleQuestionSections = targetSectionId
          ? conditionalQuestionSectionsBySectionId[targetSectionId] || []
          : [];

        questionIdsToClear = staleQuestionSections
          .map((questionSection) => Number(questionSection?.question?.question_id ?? 0))
          .filter((id) => Number.isFinite(id) && id > 0);

        if (questionIdsToClear.length) {
          questionIdsToClear.forEach((id) => {
            delete next[id];
          });
        }
      }

      return next;
    });

    if (questionIdsToClear.length) {
      setSubquestionSelectionsWithRef((prev) => {
        const next = { ...prev };
        questionIdsToClear.forEach((id) => {
          delete next[id];
        });
        return next;
      });
      clearPersistedResponsesByQuestionIds(questionIdsToClear);
    }

    if (targetSectionIdToClear) {
      clearPersistedResponsesByTargetSectionId(targetSectionIdToClear);
    }
  };

  const clearConditionalResponsesForCurrentQuestionWhenFalse = async () => {
    const sourceQuestionId = Number(questionId);
    if (!Number.isFinite(sourceQuestionId) || sourceQuestionId <= 0) return;

    const sourceRule = highestPriorityRuleBySourceQuestionId.get(sourceQuestionId);
    if (!sourceRule) return;

    const matchedNow = doesRuleMatchResponseValue(sourceRule, currentResponse);
    if (matchedNow) return;

    const targetSectionId = getTargetSectionIdFromRule(sourceRule);
    if (!targetSectionId) return;

    const staleQuestionSections = conditionalQuestionSectionsBySectionId[targetSectionId] || [];
    const questionIdsToClear = staleQuestionSections
      .map((questionSection) => Number(questionSection?.question?.question_id ?? 0))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (questionIdsToClear.length) {
      setResponsesWithRef((prev) => {
        const next = { ...prev };
        questionIdsToClear.forEach((id) => {
          delete next[id];
        });
        return next;
      });
      setSubquestionSelectionsWithRef((prev) => {
        const next = { ...prev };
        questionIdsToClear.forEach((id) => {
          delete next[id];
        });
        return next;
      });

      clearPersistedResponsesByQuestionIds(questionIdsToClear);
    }

    await clearPersistedResponsesByTargetSectionId(targetSectionId);
  };

  useEffect(() => {
    autoAdvanceHiddenQuestionKeyRef.current = "";
    setIsAutoAdvancingHiddenQuestion(false);
  }, [currentIndex, questionId]);

  useEffect(() => {
    if (!isOpen) return;

    try {
      if (!questionId || !currentItem) return;
      if (questionType === "multi_select" || questionType === "multiple_select") return;
      if (questionType === "no_response") return;

      const storedResponse = responses[questionId];
      if (hasResponseValue(storedResponse)) return;

      if (questionType === "first_last_name_response" || questionType === "first_middle_last_name_response") {
        const nextFirstName = prefillContext?.firstName ?? "";
        const nextLastName = prefillContext?.lastName ?? "";
        if (!nextFirstName && !nextLastName) return;

        setResponsesWithRef((prev) => {
          const existing = prev[questionId];
          if (hasResponseValue(existing)) return prev;

          const base =
            existing && typeof existing === "object" && !Array.isArray(existing)
              ? existing
              : getInitialResponse(questionType);

          return {
            ...prev,
            [questionId]: {
              ...base,
              ...(nextFirstName ? { first_name: nextFirstName } : {}),
              ...(nextLastName ? { last_name: nextLastName } : {}),
            },
          };
        });

        return;
      }

      const prompt = normalizePrefillPrompt(question?.question);
      const titlePrompt = normalizePrefillPrompt(question?.title);
      if (!prompt && !titlePrompt) return;

      let prefillValue;

      if (matchesPrefillPrompt(prompt, titlePrompt, AGE_PREFILL_PROMPTS)) {
        if (Number.isFinite(prefillContext?.age)) {
          prefillValue = prefillContext.age;
        }
      } else if (matchesPrefillPrompt(prompt, titlePrompt, DOB_PREFILL_PROMPTS)) {
        if (prefillContext?.dobYmd) {
          prefillValue = prefillContext.dobYmd;
        }
      } else if (isInjuryEventDatePrompt(prompt) || isInjuryEventDatePrompt(titlePrompt)) {
        if (prefillContext?.eventDateYmd) {
          prefillValue = prefillContext.eventDateYmd;
        }
      } else if (matchesPrefillPrompt(prompt, titlePrompt, REFERRAL_COMPANY_PREFILL_PROMPTS)) {
        if (prefillContext?.referralCompanyName) {
          prefillValue = prefillContext.referralCompanyName;
        }
      }

      if (prefillValue === undefined || prefillValue === null) return;
      if (typeof prefillValue === "string" && !prefillValue.trim()) return;

      setResponsesWithRef((prev) => {
        if (hasResponseValue(prev[questionId])) return prev;
        return {
          ...prev,
          [questionId]: prefillValue,
        };
      });
    } catch {
      // fall back to not pre-populating
    }
  }, [
    isOpen,
    questionId,
    questionType,
    question?.question,
    question?.title,
    currentItem,
    responses,
    prefillContext,
  ]);

  useEffect(() => {
    if (!isOpen || !isAttemptReady) return;
    if (!currentItem || !shouldAutoSkipCurrentQuestion) return;
    if (isSubmitting) return;

    const autoAdvanceKey = `${currentIndex}:${questionId}`;
    if (autoAdvanceHiddenQuestionKeyRef.current === autoAdvanceKey) return;

    autoAdvanceHiddenQuestionKeyRef.current = autoAdvanceKey;
    setIsAutoAdvancingHiddenQuestion(true);

    const advance = async () => {
      try {
        if (currentIndex === totalQuestions - 1) {
          submitAssessment();
          return;
        }

        const nextIndex = Math.min(totalQuestions - 1, currentIndex + 1);
        if (nextIndex === currentIndex) return;

        transitionToIndex(nextIndex, "forward");

        try {
          await clearConditionalResponsesForCurrentQuestionWhenFalse();
        } catch (error) {
          console.error("Failed to clear conditional responses on hidden next", error);
        }

        if (canPersistAttempts && attemptId) {
          try {
            await persistResponseAndAttemptState({
              targetIndex: nextIndex,
              status: "in_progress",
            });
          } catch (error) {
            console.error("Failed to persist hidden next navigation", error);
          }
        }
      } finally {
        setIsAutoAdvancingHiddenQuestion(false);
      }
    };

    advance();
    // These actions intentionally use the values from this auto-advance trigger render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    isAttemptReady,
    currentItem,
    shouldAutoSkipCurrentQuestion,
    isSubmitting,
    currentIndex,
    questionId,
    totalQuestions,
    canPersistAttempts,
    attemptId,
  ]);

  const selectOptions = normalizeOptions(question, normalizedPreferredLanguageCode)
    .sort((left, right) => Number(left.order ?? 0) - Number(right.order ?? 0));
  const currentVideoUrl =
    questionType === "perform_task_video" ? String(question?.hyperlink ?? "").trim() : "";
  const currentVideoSource = getVideoSource(
    currentVideoUrl,
    normalizedPreferredLanguageCode
  );
  const currentYouTubeVideoId =
    currentVideoSource.kind === "embed" ? getYouTubeEmbedVideoId(currentVideoSource.url) : "";
  const isYouTubeFallbackActive = Boolean(
    youtubeEmbedFallbackByQuestionId[String(questionId)]
  );
  const requiresVideoCompletion =
    questionType === "perform_task_video" &&
    (currentVideoSource.kind === "native" || (Boolean(currentYouTubeVideoId) && !isYouTubeFallbackActive));
  const isCurrentVideoCompleted = Boolean(videoCompletionByQuestionId[questionId]);
  const isInvalidZipcodeResponse = isZipcodeQuestion && !isValidZipcodeResponse(currentResponse);
  const isInvalidEmailResponse =
    isEmailResponseQuestion && hasCurrentResponse && !isValidEmailResponse(currentResponse);
  const isMissingRequiredResponse = isCurrentQuestionRequired && (
    isSignatureAgreementQuestion
      ? isIncompleteSignatureAgreement
      : isSignatureDrawQuestion
      ? (!hasSignatureDrawing || !hasSignatureName)
      : (!isNoResponseQuestion && !hasCurrentResponse)
  );
  const isMissingRequiredVideoCompletion =
    isCurrentQuestionRequired && requiresVideoCompletion && !isCurrentVideoCompleted;
  const optionColumnClass =
    selectOptions.length > 15
      ? "three-col"
      : selectOptions.length > 8
        ? "two-col"
        : "one-col";
  const scaleConfig = getScaleResponseConfig(questionType);
  const isScaleResponseQuestion = Boolean(scaleConfig);
  const questionTextClassName = `run-assessment-question-text ${
    isNoResponseQuestion || isScaleResponseQuestion
      ? "run-assessment-question-text-normal"
      : ""
  }`;

  const companyName =
    selectedCompany?.company_name ??
    selectedCompany?.name ??
    "Valhalla";

  const companyLogoUrl =
    selectedCompany?.logo_url ??
    selectedCompany?.logo ??
    selectedCompany?.company_logo ??
    selectedCompany?.image_url ??
    selectedCompany?.image ??
    selectedCompany?.icon_url ??
    DEFAULT_BRAND_LOGO_URL;

  const companyInitials = String(companyName)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((segment) => segment[0]?.toUpperCase())
    .join("") || "V";

  useEffect(() => {
    if (!isOpen || !requiresVideoCompletion) return;

    setVideoCompletionByQuestionId((prev) => {
      if (prev[questionId] !== undefined) return prev;
      return {
        ...prev,
        [questionId]: false,
      };
    });
  }, [isOpen, requiresVideoCompletion, questionId]);

  useEffect(() => {
    let cancelled = false;

    const destroyPlayer = () => {
      if (youtubePlayerRef.current && typeof youtubePlayerRef.current.destroy === "function") {
        youtubePlayerRef.current.destroy();
      }
      youtubePlayerRef.current = null;

      const container = youtubeContainerRef.current;
      const mountNode = youtubeMountNodeRef.current;
      if (container && mountNode && container.contains(mountNode)) {
        container.removeChild(mountNode);
      }

      youtubeMountNodeRef.current = null;
    };

    if (
      !isOpen ||
      !isAttemptReady ||
      questionType !== "perform_task_video" ||
      !currentYouTubeVideoId ||
      youtubeEmbedFallbackByQuestionId[String(questionId)]
    ) {
      destroyPlayer();
      return;
    }

    const youtubeTimeoutMs = 4500;
    const youtubeLoader = Promise.race([
      loadYouTubeIframeApi(),
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error("YouTube API timeout")), youtubeTimeoutMs);
      }),
    ]);

    youtubeLoader
      .then((YT) => {
        if (cancelled) return;

        if (!youtubeContainerRef.current) {
          return;
        }

        if (!YT?.Player) {
          setYoutubeEmbedFallbackByQuestionId((prev) => ({
            ...prev,
            [String(questionId)]: true,
          }));
          return;
        }

        destroyPlayer();

        const mountNode = document.createElement("iframe");
        mountNode.src = currentVideoSource.url;
        mountNode.title = `question-video-${questionId}`;
        mountNode.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
        mountNode.allowFullscreen = true;
        mountNode.referrerPolicy = "strict-origin-when-cross-origin";
        mountNode.style.width = "100%";
        mountNode.style.height = "100%";
        mountNode.style.border = "0";
        youtubeContainerRef.current.appendChild(mountNode);
        youtubeMountNodeRef.current = mountNode;

        try {
          const applyPreferredTranslatedCaptionTrack = (player) => {
            if (
              normalizedPreferredLanguageCode === "en" ||
              typeof player?.setOption !== "function"
            ) {
              return;
            }

            try {
              const availableTracks = typeof player?.getOption === "function"
                ? player.getOption("captions", "tracklist")
                : [];
              const normalizedTracks = Array.isArray(availableTracks) ? availableTracks : [];
              const translatedTrackId = `ta.en.${normalizedPreferredLanguageCode}`;
              const translatedTrack = normalizedTracks.find(
                (track) =>
                  String(track?.vssId ?? "").toLowerCase() === translatedTrackId ||
                  String(track?.languageCode ?? "").toLowerCase() ===
                    normalizedPreferredLanguageCode
              );

              if (translatedTrack) {
                player.setOption("captions", "track", translatedTrack);
                return;
              }

              const englishSourceTrack = normalizedTracks.find(
                (track) => String(track?.languageCode ?? "").toLowerCase() === "en"
              );
              player.setOption("captions", "track", {
                ...(englishSourceTrack ?? {
                  languageCode: "en",
                  kind: "asr",
                  vssId: "a.en",
                }),
                translationLanguage: {
                  languageCode: normalizedPreferredLanguageCode,
                },
              });
            } catch {
              // YouTube exposes caption tracks only after its captions module is ready.
            }
          };

          youtubePlayerRef.current = new YT.Player(mountNode, {
            events: {
              onReady: (event) => {
                if (typeof event.target?.loadModule === "function") {
                  event.target.loadModule("captions");
                }
                applyPreferredTranslatedCaptionTrack(event.target);
              },
              onApiChange: (event) => {
                applyPreferredTranslatedCaptionTrack(event.target);
              },
              onStateChange: (event) => {
                if (
                  event.data === YT.PlayerState.CUED ||
                  event.data === YT.PlayerState.PLAYING
                ) {
                  applyPreferredTranslatedCaptionTrack(event.target);
                }

                if (event.data === YT.PlayerState.ENDED) {
                  setVideoCompletionByQuestionId((prev) => ({
                    ...prev,
                    [questionId]: true,
                  }));
                  event.target.seekTo(0, true);
                  event.target.pauseVideo();
                }
              },
            },
          });
        } catch {
          destroyPlayer();
          setYoutubeEmbedFallbackByQuestionId((prev) => ({
            ...prev,
            [String(questionId)]: true,
          }));
        }
      })
      .catch(() => {
        destroyPlayer();
        if (!cancelled) {
          setYoutubeEmbedFallbackByQuestionId((prev) => ({
            ...prev,
            [String(questionId)]: true,
          }));
        }
      });

    return () => {
      cancelled = true;
      destroyPlayer();
    };
  }, [
    isOpen,
    isAttemptReady,
    questionType,
    currentYouTubeVideoId,
    currentVideoSource.url,
    questionId,
    normalizedPreferredLanguageCode,
    youtubeEmbedFallbackByQuestionId,
  ]);

  if (!isOpen) return null;

  if (!currentItem) {
    return (
      <div className="run-assessment-overlay">
        <div className="run-assessment-brand-badge" aria-label="company-brand">
          <div className="run-assessment-brand-logo-wrap">
            {companyLogoUrl ? (
              <img
                src={companyLogoUrl}
                alt={`${companyName} logo`}
                className="run-assessment-brand-logo"
              />
            ) : (
              <div className="run-assessment-brand-fallback">{companyInitials}</div>
            )}
          </div>
          <div className="run-assessment-brand-name">{companyName}</div>
        </div>

        <div className="run-assessment-modal">
          <div className="run-assessment-frame">
            <div className="run-assessment-persistent-header">
              <div className="run-assessment-header-top">
                <div className="run-assessment-header-left">
                  <h3>{assessmentName || "Assessment"}</h3>
                </div>
                <div className="run-assessment-header-right">
                  <button type="button" className="run-assessment-close" onClick={onClose}>✕</button>
                </div>
              </div>

              <div className="run-assessment-progress-wrap">
                <div className="run-assessment-progress-bar" aria-hidden="true">
                  <div
                    className="run-assessment-progress-fill"
                    style={{ width: "0%" }}
                  />
                </div>
                <div className="run-assessment-progress-label">
                  Question 0 of 0
                </div>
              </div>
            </div>

            <div className="run-assessment-shell run-assessment-shell-static">
              <div className="run-assessment-empty">No questions available for this assessment.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const logSelectedAnswer = (direction) => {
    console.log(`[RunAssessment] ${direction}`, {
      question_id: questionId,
      section_order: currentItem.sectionOrder,
      question_order: currentItem.questionOrder,
      question_title: question?.title ?? "",
      selected_answer: currentResponse,
    });
  };

  const transitionToIndex = (nextIndex, direction) => {
    if (isAnimatingShell) return;

    const shellElement = shellRef.current;

    if (!shellElement) {
      setCurrentIndex(nextIndex);
      return;
    }

    const shellRect = shellElement.getBoundingClientRect();
    const clone = shellElement.cloneNode(true);
    clone.classList.add("run-assessment-shell-clone");
    clone.classList.add(
      direction === "forward"
        ? "run-assessment-shell-exit-left"
        : "run-assessment-shell-exit-right"
    );

    clone.style.position = "fixed";
    clone.style.top = `${shellRect.top}px`;
    clone.style.left = `${shellRect.left}px`;
    clone.style.width = `${shellRect.width}px`;
    clone.style.height = `${shellRect.height}px`;
    clone.style.margin = "0";
    clone.style.zIndex = "1700";
    clone.style.pointerEvents = "none";
    document.body.appendChild(clone);

    setIsAnimatingShell(true);
    setShellEnterClass(
      direction === "forward"
        ? "run-assessment-shell-enter-right"
        : "run-assessment-shell-enter-left"
    );
    setCurrentIndex(nextIndex);

    window.setTimeout(() => {
      if (clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }
      setShellEnterClass("");
      setIsAnimatingShell(false);
    }, shellAnimationDurationMs);
  };

  const goPrevious = () => {
    logSelectedAnswer("Previous");
    const nextIndex = Math.max(0, currentIndex - 1);
    if (nextIndex === currentIndex) return;

    transitionToIndex(nextIndex, "backward");

    const perform = async () => {
      if (canPersistAttempts && attemptId) {
        try {
          await persistResponseAndAttemptState({
            targetIndex: nextIndex,
            status: "in_progress",
          });
        } catch (error) {
          console.error("Failed to persist previous navigation", error);
        }
      }
    };

    perform();
  };

  const goNext = () => {
    if (
      isMissingRequiredResponse ||
      isMissingRequiredVideoCompletion ||
      isInvalidZipcodeResponse ||
      isInvalidEmailResponse ||
      isIncompleteSignatureAgreement
    ) return;

    if (signatureUploadError) {
      setSignatureUploadError("");
    }

    const nextIndex = Math.min(totalQuestions - 1, currentIndex + 1);
    if (nextIndex === currentIndex) return;

    const perform = async () => {
      let agreementSigned = false;
      let agreementSent = false;

      if (isSignatureAgreementQuestion && currentSignaturePdfExporterRef.current) {
        try {
          const signatureExportResult = await currentSignaturePdfExporterRef.current();
          agreementSent = signatureExportResult?.agreementSent === true;
        } catch (error) {
          setSignatureUploadError(
            getPresignedUploadErrorMessage(error, "Signature agreement upload")
          );
          console.error("Failed to upload signature agreement PDF", error);
          return;
        }
      }

      transitionToIndex(nextIndex, "forward");

      try {
        await clearConditionalResponsesForCurrentQuestionWhenFalse();
      } catch (error) {
        console.error("Failed to clear conditional responses on next", error);
      }

      if (canPersistAttempts && attemptId) {
        try {
          await persistResponseAndAttemptState({
            targetIndex: nextIndex,
            status: "in_progress",
          });
          if (isSignatureAgreementQuestion) {
            agreementSigned = true;
          }
        } catch (error) {
          console.error("Failed to persist next navigation", error);
        }

        if (isSignatureAgreementQuestion) {
          try {
            await updateAttemptAgreementStatus({
              requestFn,
              attemptId,
              agreementSigned,
              agreementSent,
            });
          } catch (error) {
            console.error("Failed to update signature agreement status", error);
          }
        }
      }
    };

    perform();
  };

  const submitAssessment = () => {
    if (isSubmitting) return;
    if (
      isMissingRequiredResponse ||
      isMissingRequiredVideoCompletion ||
      isInvalidZipcodeResponse ||
      isInvalidEmailResponse ||
      isIncompleteSignatureAgreement
    ) return;

    logSelectedAnswer("Submit");
    console.log("[RunAssessment] Submit payload", {
      assessment_name: assessmentName || "Assessment",
      total_questions: totalQuestions,
      responses,
    });

    const perform = async () => {
      setIsSubmitting(true);
      try {
        if (canPersistAttempts && attemptId) {
          try {
            await persistResponseAndAttemptState({
              targetIndex: currentIndex,
              status: "completed",
              markComplete: true,
            });

            try {
              await persistAssessment1001AttemptMetrics(attemptId);
            } catch (metricError) {
              console.error("Failed to update assessment 1001 attempt metrics", metricError);
            }

            try {
              const payload = await fetchAttemptTotalScore(attemptId);
              const totalScoreValue = toFiniteNumberOrNull(payload?.total_score) ?? 0;

              const endpoint = `${PATIENT_ASSESSMENT_ATTEMPTS_API}${attemptId}/`;

              const parseJsonOrText = async (response) => {
                try {
                  return await response.json();
                } catch {
                  try {
                    return { detail: await response.text() };
                  } catch {
                    return {};
                  }
                }
              };

              const patchAttempt = async (body) => {
                const res = await requestFn(endpoint, {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    status: "completed",
                    ...body,
                  }),
                });

                const parsedBody = await parseJsonOrText(res);
                return { res, body: parsedBody };
              };

              const didSetFinalScore = (attemptPayload) => {
                const updatedValue = toFiniteNumberOrNull(attemptPayload?.final_score);
                if (updatedValue === null) return false;
                return Math.abs(updatedValue - totalScoreValue) < 0.0001;
              };

              const attempts = [
                { field: "final_score", value: totalScoreValue },
                { field: "score", value: totalScoreValue },
                { field: "total_score", value: totalScoreValue },
              ];

              let lastFailure = null;
              for (const attempt of attempts) {
                const { res, body } = await patchAttempt({ [attempt.field]: attempt.value });
                if (!res.ok) {
                  lastFailure = new Error(
                    `Attempt score update failed via ${attempt.field} (${res.status}) ${JSON.stringify(body)}`
                  );
                  continue;
                }

                if (didSetFinalScore(body)) {
                  lastFailure = null;
                  break;
                }

                // PATCH succeeded but didn't actually set final_score (likely read-only/ignored).
                lastFailure = new Error(
                  `Attempt score update via ${attempt.field} did not update final_score. Response: ${JSON.stringify(body)}`
                );
              }

              if (lastFailure) {
                throw lastFailure;
              }
            } catch (scoreError) {
              console.error("Failed to update attempt final score", scoreError);
            }

            if (generateReportOnSubmit && numericCompanyId > 0) {
              void requestFn(
                `${API_BASE}/api/generate-report/${numericCompanyId}/${attemptId}`,
                { keepalive: true }
              ).catch(() => {});
            }
          } catch (error) {
            console.error("Failed to persist submit", error);
          }
        }
      } finally {
        setIsSubmitting(false);
      }

      onSubmitComplete?.();
      onClose?.();
    };

    perform();
  };

  if (!isAttemptReady || isResolvingRequestedStart || isAutoAdvancingHiddenQuestion) {
    return (
      <div className="run-assessment-overlay">
        <div className="run-assessment-modal">
          <div className="run-assessment-frame">
            <div className="run-assessment-persistent-header">
              <div className="run-assessment-header-top">
                <div className="run-assessment-header-left">
                  <h3>{assessmentName || "Assessment"}</h3>
                </div>
                <div className="run-assessment-header-right">
                  <button type="button" className="run-assessment-close" onClick={onClose}>✕</button>
                </div>
              </div>
            </div>
            <div className="run-assessment-shell run-assessment-shell-static">
              <div className="run-assessment-empty">
                {isAutoAdvancingHiddenQuestion
                  ? "Loading next question..."
                  : isResolvingRequestedStart
                  ? "Opening selected question..."
                  : "Loading assessment progress..."}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderResponseField = () => {
    if (scaleConfig) {
      const parsedCurrent = Number(currentResponse);
      const sliderValue = Number.isFinite(parsedCurrent)
        ? parsedCurrent
        : scaleConfig.defaultValue;
      const percent =
        scaleConfig.max > scaleConfig.min
          ? ((sliderValue - scaleConfig.min) / (scaleConfig.max - scaleConfig.min)) * 100
          : 50;

      return (
        <div className="run-assessment-slider-wrap">
          <div className="run-assessment-slider-control">
            <input
              type="range"
              min={scaleConfig.min}
              max={scaleConfig.max}
              step={scaleConfig.step}
              value={sliderValue}
              onChange={(event) => setResponse(Number(event.target.value))}
              className="run-assessment-slider"
            />
            <div
              className="run-assessment-slider-thumb-value"
              style={{ left: `${percent}%` }}
              aria-hidden="true"
            >
              {sliderValue}
            </div>
          </div>
          <div className="run-assessment-slider-meta">
            <span>{scaleConfig.min}</span>
            <span>{scaleConfig.max}</span>
          </div>
        </div>
      );
    }

    switch (questionType) {
      case "signature_agreement":
        return (
          <AgreementSignatureField
            value={currentResponse}
            onChange={setResponse}
            agreementSource={
              question?.options ?? question?.choices ?? question?.question_type?.options ?? null
            }
            documentName={String(question?.title ?? question?.question ?? "Agreement Document")}
            questionText={question?.question}
            patientData={patientTemplateData}
            documentTypeId={getCaseInsensitiveValue(question?.choices, "document_type_id")}
            patientId={numericPatientId || 1002}
            companyId={numericCompanyId || 1000}
            assessmentAttemptId={attemptId || numericAttemptIdOverride || 46}
            uploadRequestFn={requestFn}
            emailRecipient={resolvedSignatureAgreementEmailRecipient}
            onRegisterPdfExporter={(exporter) => {
              currentSignaturePdfExporterRef.current = exporter;
            }}
            isRequired={isCurrentQuestionRequired}
            questionTextClassName={questionTextClassName}
            preferredLanguageCode={normalizedPreferredLanguageCode}
          />
        );
      case "zipcode":
      case "zip_code":
        return (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={5}
            value={normalizeZipcodeInput(currentResponse)}
            onChange={(event) => setResponse(normalizeZipcodeInput(event.target.value))}
            className="run-assessment-input"
          />
        );
      case "free_response":
        return (
          <input
            type="text"
            value={String(currentResponse ?? "")}
            onChange={(event) => setResponse(event.target.value)}
            className="run-assessment-input"
          />
        );
      case "email_response":
        return (
          <input
            type="email"
            value={String(currentResponse ?? "")}
            onChange={(event) => setResponse(event.target.value)}
            className="run-assessment-input"
            aria-invalid={isInvalidEmailResponse}
            aria-describedby={isInvalidEmailResponse ? "run-assessment-email-error" : undefined}
          />
        );
      case "date_response":
        return (
          <input
            type="date"
            lang={normalizedPreferredLanguageCode}
            value={String(currentResponse ?? "")}
            onChange={(event) => setResponse(event.target.value)}
            className="run-assessment-input"
          />
        );
      case "number_response":
        return (
          <input
            type="number"
            value={String(currentResponse ?? "")}
            onChange={(event) => setResponse(event.target.value)}
            className="run-assessment-input"
          />
        );
      case "phone_number_response":
        return (
          <input
            type="tel"
            value={formatPhoneInput(currentResponse)}
            onChange={(event) => setResponse(formatPhoneInput(event.target.value))}
            className="run-assessment-input"
          />
        );
      case "first_last_name_response":
        return (
          <div className="run-assessment-name-grid two-col">
            <input
              type="text"
              placeholder="First Name"
              value={currentResponse?.first_name ?? ""}
              onChange={(event) =>
                setResponse({
                  ...currentResponse,
                  first_name: event.target.value,
                })
              }
              className="run-assessment-input"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={currentResponse?.last_name ?? ""}
              onChange={(event) =>
                setResponse({
                  ...currentResponse,
                  last_name: event.target.value,
                })
              }
              className="run-assessment-input"
            />
          </div>
        );
      case "first_middle_last_name_response":
        return (
          <div className="run-assessment-name-grid three-col">
            <input
              type="text"
              placeholder="First Name"
              value={currentResponse?.first_name ?? ""}
              onChange={(event) =>
                setResponse({
                  ...currentResponse,
                  first_name: event.target.value,
                })
              }
              className="run-assessment-input"
            />
            <input
              type="text"
              placeholder="Middle Name"
              value={currentResponse?.middle_name ?? ""}
              onChange={(event) =>
                setResponse({
                  ...currentResponse,
                  middle_name: event.target.value,
                })
              }
              className="run-assessment-input"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={currentResponse?.last_name ?? ""}
              onChange={(event) =>
                setResponse({
                  ...currentResponse,
                  last_name: event.target.value,
                })
              }
              className="run-assessment-input"
            />
          </div>
        );
      case "free_response_long":
        return (
          <textarea
            rows={5}
            value={String(currentResponse ?? "")}
            onChange={(event) => setResponse(event.target.value)}
            className="run-assessment-textarea"
          />
        );
      case "signature_draw":
        return (
          <SignatureDrawField
            value={currentResponse}
            onChange={setResponse}
          />
        );
      case "no_response":
        return null;
      case "perform_task_video": {
        const videoUrl = currentVideoUrl;
        const videoSource = currentVideoSource;

        if (!videoUrl) {
          return (
            <div className="run-assessment-video-empty">
              No video link is available for this question.
            </div>
          );
        }

        return (
          <div className="run-assessment-video-wrap">
            {videoSource.kind === "native" ? (
              <video
                className="run-assessment-video-frame"
                controls
                playsInline
                preload="metadata"
                src={videoSource.url}
                onEnded={() =>
                  setVideoCompletionByQuestionId((prev) => ({
                    ...prev,
                    [questionId]: true,
                  }))
                }
              >
                Your browser does not support the video tag.
              </video>
            ) : videoSource.kind === "embed" && currentYouTubeVideoId && !isYouTubeFallbackActive ? (
              <div
                ref={youtubeContainerRef}
                className="run-assessment-video-frame"
              />
            ) : videoSource.kind === "embed" ? (
              <iframe
                title={`question-video-${questionId}`}
                src={videoSource.url}
                className="run-assessment-video-frame"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            ) : (
              <div className="run-assessment-video-empty">
                This video host blocks in-app playback. Use the link below to open it.
              </div>
            )}
          </div>
        );
      }
      case "multi_select":
      case "multiple_select":
        if (selectOptions.length > 0) {
          const selectedItems = Array.isArray(currentResponse) ? currentResponse : [];

          const toggleOption = (optionItem) => {
            const optionOrder = Number(optionItem.order);
            const parentOptionId = buildSelectableItemIdentity(optionItem);
            const exists = selectedItems.some((item) => matchesSelectableOption(item, optionItem));
            const shouldShowSubquestion = shouldAllowSubquestionForAnswerValue(optionItem?.value);
            const isExclusiveNoneOption = isExclusiveNoneMultiSelectOption(optionItem);

            if (exists) {
              setSubquestionSelectionsWithRef((prev) => {
                const currentSelections = prev[questionId] ?? {};
                if (!(parentOptionId in currentSelections)) return prev;

                const nextSelections = { ...currentSelections };
                delete nextSelections[parentOptionId];

                if (!Object.keys(nextSelections).length) {
                  const next = { ...prev };
                  delete next[questionId];
                  return next;
                }

                return {
                  ...prev,
                  [questionId]: nextSelections,
                };
              });

              if (
                activeSubquestionPopup?.questionId === questionId &&
                activeSubquestionPopup?.parentOptionId === parentOptionId
              ) {
                setActiveSubquestionPopup(null);
              }

              setResponse((prevResponse) => {
                const currentItems = Array.isArray(prevResponse) ? prevResponse : [];
                return currentItems.filter((item) => !matchesSelectableOption(item, optionItem));
              });
              return;
            }

            setResponse((prevResponse) => {
              const currentItems = Array.isArray(prevResponse) ? prevResponse : [];
              if (currentItems.some((item) => matchesSelectableOption(item, optionItem))) {
                return currentItems;
              }

              const nextItem = {
                order: optionOrder,
                ...getStoredOptionFields(optionItem, normalizedPreferredLanguageCode),
                value: optionItem.value,
                ...(Object.prototype.hasOwnProperty.call(optionItem ?? {}, "category")
                  ? { category: optionItem.category }
                  : {}),
              };

              if (isExclusiveNoneOption) {
                return [nextItem];
              }

              const filteredItems = currentItems.filter(
                (item) => !isExclusiveNoneMultiSelectOption(item)
              );

              return [
                ...filteredItems,
                nextItem,
              ];
            });

            if (
              currentQuestionHasSubquestion &&
              shouldShowSubquestion &&
              currentSubQuestionOptions.length > 0
            ) {
              setActiveSubquestionPopup({
                questionId,
                parentOptionId,
                parentOptionLabel: String(optionItem?.option ?? "").trim(),
              });
            }
          };

          return (
            <div className={`run-assessment-radio-group ${optionColumnClass}`}>
              {selectOptions.map((optionItem) => {
                const optionIdentity = buildSelectableItemIdentity(optionItem);
                const checked = selectedItems.some((item) => matchesSelectableOption(item, optionItem));
                const selectedSubquestionResponse =
                  filteredCurrentQuestionSubquestionSelections[optionIdentity] ??
                  selectedItems.find(
                    (item) =>
                      matchesSelectableOption(item, optionItem) &&
                      shouldAllowSubquestionForAnswerValue(item?.value)
                  )?.sub_answer ??
                  null;

                return (
                  renderSelectableOptionRow({
                    optionItem,
                    inputType: "checkbox",
                    inputName: `question-${questionId}-multi`,
                    checked,
                    value: optionIdentity,
                    onChange: () => toggleOption(optionItem),
                    trailingContent:
                      checked && selectedSubquestionResponse?.option ? (
                        <span className="run-assessment-option-subquestion-answer">
                          {getLocalizedOptionLabel(
                            selectedSubquestionResponse,
                            normalizedPreferredLanguageCode
                          )}
                        </span>
                      ) : null,
                  })
                );
              })}
            </div>
          );
        }

        return (
          <input
            type="text"
            value={String(currentResponse ?? "")}
            onChange={(event) => setResponse(event.target.value)}
            className="run-assessment-input"
          />
        );
      case "single_select":
      case "general_occurence_single_answer":
        if (selectOptions.length > 0) {
          const selectedOrder = getSelectedOptionOrder(currentResponse, selectOptions);

          return (
            <div className={`run-assessment-radio-group ${optionColumnClass}`}>
              {selectOptions.map((optionItem) => {
                const optionOrder = Number(optionItem.order);
                const checked = Number.isFinite(selectedOrder) && selectedOrder === optionOrder;

                return renderSelectableOptionRow({
                  optionItem,
                  inputType: "radio",
                  inputName: `question-${questionId}`,
                  checked,
                  value: optionOrder,
                  onChange: () =>
                    setResponse({
                      order: optionOrder,
                      ...getStoredOptionFields(optionItem, normalizedPreferredLanguageCode),
                      value: optionItem.value,
                    }),
                });
              })}
            </div>
          );
        }

        return (
          <input
            type="text"
            value={String(currentResponse ?? "")}
            onChange={(event) => setResponse(event.target.value)}
            className="run-assessment-input"
          />
        );
      default:
        if (selectOptions.length > 0) {
          const selectedOrder = getSelectedOptionOrder(currentResponse, selectOptions);

          return (
            <div className={`run-assessment-radio-group ${optionColumnClass}`}>
              {selectOptions.map((optionItem) => {
                const optionOrder = Number(optionItem.order);
                const checked = Number.isFinite(selectedOrder) && selectedOrder === optionOrder;
                return renderSelectableOptionRow({
                  optionItem,
                  inputType: "radio",
                  inputName: `question-${questionId}`,
                  checked,
                  value: optionOrder,
                  onChange: () =>
                    setResponse({
                      order: optionOrder,
                      ...getStoredOptionFields(optionItem, normalizedPreferredLanguageCode),
                      value: optionItem.value,
                    }),
                });
              })}
            </div>
          );
        }

        return (
          <input
            type="text"
            value={String(currentResponse ?? "")}
            onChange={(event) => setResponse(event.target.value)}
            className="run-assessment-input"
          />
        );
    }
  };

  return (
    <div className="run-assessment-overlay">
      <div className="run-assessment-brand-badge" aria-label="company-brand">
        <div className="run-assessment-brand-logo-wrap">
          {companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt={`${companyName} logo`}
              className="run-assessment-brand-logo"
            />
          ) : (
            <div className="run-assessment-brand-fallback">{companyInitials}</div>
          )}
        </div>
        <div className="run-assessment-brand-name">{companyName}</div>
      </div>

      <div className="run-assessment-modal">
        <div className="run-assessment-frame">
          <div className="run-assessment-persistent-header">
            <div className="run-assessment-header-top">
              <div className="run-assessment-header-left">
                <h3>{assessmentName || "Assessment"}</h3>
              </div>
              <div className="run-assessment-header-right">
                {showMetadataToggle && (
                  <label className="run-assessment-metadata-toggle">
                    <input
                      type="checkbox"
                      checked={showMetadata}
                      onChange={(event) => setShowMetadata(event.target.checked)}
                    />
                    <span>Show Metadata</span>
                  </label>
                )}
                <button type="button" className="run-assessment-close" onClick={onClose}>✕</button>
              </div>
            </div>

            <div className="run-assessment-progress-wrap">
              <div className="run-assessment-progress-bar" aria-hidden="true">
                <div
                  className="run-assessment-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="run-assessment-progress-label">
                {isSpanish ? "Pregunta" : "Question"} {currentIndex + 1} of {totalQuestions}
              </div>
            </div>
          </div>

        <div
          ref={shellRef}
          className={`run-assessment-shell ${shellEnterClass}`}
        >
          <div className="run-assessment-card">
            {showMetadata && (
              <>
                <div className="run-assessment-meta">
                  <span>Section {currentItem.sectionOrder}: {currentItem.sectionTitle}</span>
                  <span>Section ID: {currentItem.sectionId}</span>
                  <span>Question {currentIndex + 1} of {totalQuestions}</span>
                </div>

                <div className="run-assessment-submeta">
                  <span>Question ID: {questionId}</span>
                  <span>Section Question Order: {currentItem.questionOrder && currentItem.questionOrder > 0 ? currentItem.questionOrder : "null"}</span>
                  <span>Title: {question?.title || "Untitled Question"}</span>
                  <span>Type: {questionType || "—"}</span>
                  <span>Has Subquestion: {currentQuestionHasSubquestion ? "Yes" : "No"}</span>
                  {currentQuestionHasSubquestion && (
                    <span>Subquestion Type: {currentSubQuestionTypeLabel || "—"}</span>
                  )}
                </div>
              </>
            )}
            {(isNoResponseQuestion || isScaleResponseQuestion) && (
              <h4 className="run-assessment-question-title">
                {question?.title || "Untitled Question"}
              </h4>
            )}

            {isSignatureAgreementQuestion ? (
              <>
                <div className="run-assessment-response-area">
                  {renderResponseField()}
                </div>
              </>
            ) : (
              <>
                <p className={questionTextClassName}>
                  {question?.question || "No question text provided."}
                  {isCurrentQuestionRequired && (
                    <span className="run-assessment-required-marker" aria-label="required"> *</span>
                  )}
                </p>

                <div className="run-assessment-response-area">
                  {renderResponseField()}
                </div>
              </>
            )}

            {activeSubquestionPopup &&
              activeSubquestionPopup.questionId === questionId &&
              currentQuestionHasSubquestion &&
              Array.isArray(currentResponse) &&
              currentResponse.some(
                (item) =>
                  buildSelectableItemIdentity(item) === activeSubquestionPopup.parentOptionId &&
                  shouldAllowSubquestionForAnswerValue(item?.value)
              ) &&
              currentSubQuestionOptions.length > 0 && (
                <div className="run-assessment-subquestion-overlay" role="presentation">
                  <div
                    className="run-assessment-subquestion-popup"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Subquestion prompt"
                  >
                    <div className="run-assessment-subquestion-popup-header">
                      <div className="run-assessment-subquestion-popup-title">
                        {activeSubquestionPopup.parentOptionLabel
                          ? activeSubquestionPopup.parentOptionLabel
                          : ""}
                      </div>
                    </div>

                    <div className="run-assessment-subquestion-popup-prompt">
                      {currentSubQuestionPrompt || "Please answer the follow-up question."}
                    </div>

                    <div className="run-assessment-subquestion-popup-options">
                      {currentSubQuestionOptions.map((optionItem) => {
                        const optionIdentity = buildSelectableItemIdentity(optionItem);
                        const isSelected =
                          buildSelectableItemIdentity(activeSubquestionSelection) === optionIdentity;

                        return (
                          <button
                            type="button"
                            key={`subquestion-${optionIdentity}`}
                            className={`run-assessment-subquestion-option ${
                              isSelected ? "run-assessment-subquestion-option-selected" : ""
                            }`}
                            onClick={() => {
                              const nextSubAnswer = {
                                value: optionItem?.value ?? optionItem?.option ?? "",
                                ...getStoredOptionFields(
                                  optionItem,
                                  normalizedPreferredLanguageCode
                                ),
                              };

                              setSubquestionSelectionsWithRef((prev) => ({
                                ...prev,
                                [questionId]: {
                                  ...(prev[questionId] ?? {}),
                                  [activeSubquestionPopup.parentOptionId]: {
                                    order: Number(optionItem?.order),
                                    ...getStoredOptionFields(
                                      optionItem,
                                      normalizedPreferredLanguageCode
                                    ),
                                    value: optionItem?.value ?? optionItem?.option ?? "",
                                  },
                                },
                              }));

                              setResponse((prevResponse) => {
                                const currentItems = Array.isArray(prevResponse) ? prevResponse : [];
                                return currentItems.map((item) =>
                                  buildSelectableItemIdentity(item) === activeSubquestionPopup.parentOptionId
                                    ? {
                                        ...item,
                                        sub_answer: nextSubAnswer,
                                      }
                                    : item
                                );
                              });

                              setActiveSubquestionPopup(null);
                            }}
                          >
                            {optionItem.option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

            {signatureUploadError ? (
              <div className="run-assessment-inline-error" role="alert">
                {signatureUploadError}
              </div>
            ) : null}

            {isInvalidEmailResponse ? (
              <div
                id="run-assessment-email-error"
                className="run-assessment-inline-error"
                role="alert"
              >
                {isSpanish ? "Ingrese una dirección de correo electrónico válida." : "Enter a valid email address."}
              </div>
            ) : null}

            {activeOptionMoreInfoCard?.questionId === questionId ? (
              <div
                className={`run-assessment-option-more-info-overlay ${
                  activeOptionMoreInfoCard?.trigger === "hover"
                    ? "run-assessment-option-more-info-overlay-hover"
                    : "run-assessment-option-more-info-overlay-click"
                }`}
                role="presentation"
                onClick={() => {
                  if (activeOptionMoreInfoCard?.trigger === "click") {
                    setActiveOptionMoreInfoCard(null);
                  }
                }}
              >
                <div
                  className={`run-assessment-option-more-info-card ${
                    activeOptionMoreInfoCard?.trigger === "hover"
                      ? "run-assessment-option-more-info-card-hover"
                      : "run-assessment-option-more-info-card-click"
                  }`}
                  role="dialog"
                  aria-modal="true"
                  aria-label={`${activeOptionMoreInfoCard?.optionLabel || "Option"} more info`}
                  style={{
                    left: `${activeOptionMoreInfoCard?.anchorPosition?.left ?? 12}px`,
                    top: `${activeOptionMoreInfoCard?.anchorPosition?.top ?? 12}px`,
                    width: `${activeOptionMoreInfoCard?.anchorPosition?.width ?? 320}px`,
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <div className="run-assessment-option-more-info-card-header">
                    <div className="run-assessment-option-more-info-card-title-wrap">
                      <div className="run-assessment-option-more-info-card-title">
                        {activeOptionMoreInfoCard?.optionLabel || "Option"}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="run-assessment-option-more-info-card-close"
                      aria-label="Close more info"
                      onClick={() => setActiveOptionMoreInfoCard(null)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="run-assessment-option-more-info-card-body">
                    {activeOptionMoreInfoCard?.infoText}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="run-assessment-nav">
              <button
                type="button"
                className="run-assessment-link run-assessment-link-prev"
                onClick={goPrevious}
                disabled={currentIndex === 0}
              >
                <span className="run-assessment-arrow" aria-hidden="true">←</span>
                <span>{isSpanish ? "Anterior" : "Previous"}</span>
              </button>
              <button
                type="button"
                className="run-assessment-link run-assessment-link-next"
                onClick={currentIndex === totalQuestions - 1 ? submitAssessment : goNext}
                disabled={
                  isSubmitting ||
                  isMissingRequiredResponse ||
                  isMissingRequiredVideoCompletion ||
                  isInvalidZipcodeResponse ||
                  isInvalidEmailResponse ||
                  isIncompleteSignatureAgreement
                }
              >
                <span>
                  {currentIndex === totalQuestions - 1
                    ? isSpanish
                      ? "Enviar"
                      : "SUBMIT"
                    : isSpanish
                      ? "Siguiente"
                      : "Next"}
                </span>
                {currentIndex !== totalQuestions - 1 && (
                  <span className="run-assessment-arrow" aria-hidden="true">→</span>
                )}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
